// The condition catalog. Types and matching logic live alongside in
// conditions-types.ts and conditions.ts — this file is data only.
//
// Entries below the hand-written first block were researched against dbSNP,
// the GWAS Catalog, ClinVar, PharmGKB and CPIC, then machine-validated for
// the invariant that broke rs334: effectAllele must be one of `alleles`.

import type { Condition } from './conditions-types';

export const CONDITIONS: Condition[] = [
  {
    id: 't2d',
    name: 'Type 2 diabetes',
    aliases: ['diabetes', 'T2D', 'blood sugar', 'insulin resistance', 'TCF7L2'],
    category: 'metabolic',
    impact: 'notable',
    summary:
      'Common polygenic condition. Hundreds of loci contribute; the three below are among the earliest and most consistently replicated, but together they explain only a small share of heritability.',
    sources: 'GWAS Catalog (EFO_0001360); DIAGRAM consortium meta-analyses',
    markers: [
      {
        rsid: 'rs7903146',
        gene: 'TCF7L2',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'The strongest common T2D variant known. Each T copy is associated with roughly 1.4× the odds of T2D in European-ancestry cohorts.',
      },
      {
        rsid: 'rs1801282',
        gene: 'PPARG',
        alleles: ['C', 'G'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'Pro12Ala. The common C (Pro) allele carries slightly higher risk; the rarer G (Ala) allele is mildly protective (~0.85×).',
      },
      {
        rsid: 'rs5219',
        gene: 'KCNJ11',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'E23K, affecting a pancreatic potassium channel. Modest per-allele increase in odds (~1.15×).',
      },
    ],
  },
  {
    id: 'obesity',
    name: 'Obesity / body-mass index',
    aliases: ['BMI', 'weight', 'FTO', 'appetite'],
    category: 'metabolic',
    impact: 'modest',
    summary:
      'Common variants shift average BMI by small amounts. Effects are population averages and are strongly modified by diet and activity — they are not individual predictions.',
    sources: 'GWAS Catalog (EFO_0004340); GIANT consortium',
    markers: [
      {
        rsid: 'rs9939609',
        gene: 'FTO',
        alleles: ['T', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'The best-known BMI variant. Each A copy is associated with roughly +0.4 kg/m² BMI on average — about 1 kg for a typical adult.',
      },
      {
        rsid: 'rs17782313',
        gene: 'MC4R',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'Near a satiety-signalling receptor. Each C copy is associated with roughly +0.2 kg/m² BMI.',
      },
    ],
  },
  {
    id: 'cad',
    name: 'Coronary artery disease',
    aliases: ['heart disease', 'CAD', 'heart attack', 'myocardial infarction', '9p21', 'atherosclerosis'],
    category: 'cardiovascular',
    impact: 'notable',
    summary:
      'The 9p21 locus was the first robust common-variant association for heart disease and replicates across many populations. It acts independently of cholesterol and blood pressure.',
    sources: 'GWAS Catalog (EFO_0001645); CARDIoGRAMplusC4D',
    markers: [
      {
        rsid: 'rs10757278',
        gene: 'CDKN2B-AS1 (9p21)',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'Each G copy is associated with roughly 1.25× the odds of coronary artery disease.',
      },
      {
        rsid: 'rs1333049',
        gene: 'CDKN2B-AS1 (9p21)',
        alleles: ['G', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'A close proxy for rs10757278 in the same haplotype block — the two carry largely the same signal, not two independent ones.',
      },
    ],
  },
  {
    id: 'afib',
    name: 'Atrial fibrillation',
    aliases: ['AF', 'irregular heartbeat', 'arrhythmia', 'PITX2'],
    category: 'cardiovascular',
    impact: 'notable',
    summary:
      'The 4q25 locus near PITX2 is the strongest common-variant association for atrial fibrillation.',
    sources: 'GWAS Catalog (EFO_0000275); AFGen consortium',
    markers: [
      {
        rsid: 'rs2200733',
        gene: '4q25 (near PITX2)',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'Each T copy is associated with roughly 1.7× the odds of atrial fibrillation — a large effect for a common variant.',
      },
    ],
  },
  {
    id: 'celiac',
    name: 'Celiac disease',
    aliases: ['gluten', 'coeliac', 'HLA-DQ2', 'wheat'],
    category: 'autoimmune',
    impact: 'notable',
    summary:
      'Celiac disease is almost entirely confined to people carrying HLA-DQ2 or DQ8. The tag SNP below captures DQ2.5 imperfectly — it is a screening hint, not an HLA type.',
    caveat:
      'Roughly 30–40% of the general population carries DQ2/DQ8 but only ~1% develops celiac disease, so a positive tag is very weakly predictive. A negative result on *full HLA typing* is the informative direction — but this tag only covers DQ2.5. It does not detect DQ8 or DQ2.2, which between them account for roughly 5–10% of celiac patients, so a negative call here is not a rule-out. Proper HLA typing and serology are the real tests.',
    sources: 'GWAS Catalog (EFO_0001060); Dubois et al. 2010',
    markers: [
      {
        rsid: 'rs2187668',
        gene: 'HLA-DQA1',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'Tags the HLA-DQ2.5 haplotype, the main celiac risk type. Carrying it is close to a precondition for the disease, but far from sufficient.',
      },
      {
        rsid: 'rs3184504',
        gene: 'SH2B3',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'A non-HLA celiac locus, also associated with several other autoimmune conditions. Small individual effect.',
      },
    ],
  },
  {
    id: 'ra',
    name: 'Rheumatoid arthritis',
    aliases: ['RA', 'arthritis', 'joint inflammation', 'PTPN22', 'autoimmune'],
    category: 'autoimmune',
    impact: 'notable',
    summary:
      'PTPN22 R620W is one of the strongest non-HLA autoimmune variants, and is shared across rheumatoid arthritis, type 1 diabetes, and lupus.',
    sources: 'GWAS Catalog (EFO_0000685); Okada et al. 2014',
    markers: [
      {
        rsid: 'rs2476601',
        gene: 'PTPN22',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'R620W. Each A copy is associated with roughly 1.8× the odds of seropositive rheumatoid arthritis in European-ancestry cohorts. The variant is rare in East Asian populations.',
      },
    ],
  },
  {
    id: 't1d',
    name: 'Type 1 diabetes',
    aliases: ['T1D', 'juvenile diabetes', 'insulin-dependent', 'autoimmune diabetes'],
    category: 'autoimmune',
    impact: 'notable',
    summary:
      'Risk is dominated by the HLA region, which consumer arrays cover poorly. The non-HLA markers below add real but comparatively small amounts.',
    caveat:
      'Without HLA typing, any type 1 diabetes assessment from an array is badly incomplete — the largest single contributor is missing.',
    sources: 'GWAS Catalog (EFO_0001359); T1DGC',
    markers: [
      {
        rsid: 'rs2476601',
        gene: 'PTPN22',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect: 'Each A copy is associated with roughly 1.9× the odds of type 1 diabetes.',
      },
      {
        rsid: 'rs1990760',
        gene: 'IFIH1',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'A viral-sensing gene; the T (Ala946Thr) allele modestly raises risk (~1.2×).',
      },
    ],
  },
  {
    id: 'crohns',
    name: "Crohn's disease",
    aliases: ['IBD', 'inflammatory bowel disease', 'NOD2', 'colitis', 'gut'],
    category: 'autoimmune',
    impact: 'notable',
    summary:
      'NOD2 variants were the first Crohn\'s associations found and carry unusually large effects for common disease. IL23R shows the opposite direction — a protective variant.',
    sources: 'GWAS Catalog (EFO_0000384); IIBDGC',
    markers: [
      {
        rsid: 'rs2066844',
        gene: 'NOD2',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'R702W. One copy roughly doubles the odds of Crohn\'s disease; two copies raise it considerably further.',
      },
      {
        rsid: 'rs11209026',
        gene: 'IL23R',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'protective',
        effect:
          'R381Q. Each A copy is associated with roughly 0.4× the odds of Crohn\'s disease — one of the clearest protective variants in complex disease genetics.',
      },
    ],
  },
  {
    id: 'amd',
    name: 'Age-related macular degeneration',
    aliases: ['AMD', 'macular', 'vision loss', 'eye', 'CFH'],
    category: 'neurological',
    impact: 'notable',
    summary:
      'Unusually strong common-variant effects: two loci account for a large share of population risk, which is rare in complex disease.',
    sources: 'GWAS Catalog (EFO_0001365); Fritsche et al. 2016',
    markers: [
      {
        rsid: 'rs1061170',
        gene: 'CFH',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'Y402H in complement factor H. Each C copy is associated with roughly 2.5× the odds of advanced AMD.',
      },
      {
        rsid: 'rs10490924',
        gene: 'ARMS2/HTRA1',
        alleles: ['G', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'A second large-effect locus, roughly comparable in magnitude to CFH and largely independent of it.',
      },
    ],
  },
  {
    id: 'alzheimers',
    name: "Alzheimer's disease (late-onset)",
    aliases: ['dementia', 'APOE', 'memory', 'cognitive decline'],
    category: 'neurological',
    impact: 'clinical',
    summary:
      'APOE is the dominant common-variant risk factor for late-onset Alzheimer\'s disease. The two SNPs below jointly define the ε2/ε3/ε4 alleles.',
    caveat:
      'i-seng deliberately does NOT compute your APOE ε genotype. Combining the two calls is mechanically straightforward — only the double heterozygote is formally ambiguous, and it is conventionally read ε2/ε4 because the ε1 haplotype is essentially never seen. The reason for withholding it is not technical: a result of that personal weight should arrive with genetic counselling, not from a hobby tool. The two raw calls are shown; interpreting them together is a conversation to have with a professional.',
    sources: 'ClinVar; Farrer et al. 1997; GWAS Catalog (EFO_0000249)',
    markers: [
      {
        rsid: 'rs429358',
        gene: 'APOE',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'The C allele is the ε4-defining change. ε4 is associated with substantially increased and earlier-onset risk, but it is neither necessary nor sufficient — many ε4 carriers never develop the disease.',
      },
      {
        rsid: 'rs7412',
        gene: 'APOE',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'protective',
        effect:
          'The T allele is the ε2-defining change. ε2 is associated with reduced Alzheimer\'s risk — though ε2/ε2 homozygotes carry a raised risk of type III hyperlipoproteinemia, a low-penetrance association that affects under ~10% of them and usually needs a second trigger.',
      },
    ],
  },
  {
    id: 'parkinsons',
    name: "Parkinson's disease",
    aliases: ['PD', 'tremor', 'SNCA', 'movement disorder'],
    category: 'neurological',
    impact: 'modest',
    summary:
      'Common variants near SNCA (alpha-synuclein) shift risk modestly. The high-penetrance familial variants — LRRK2 G2019S and GBA N409S — are directly genotyped by some consumer platforms (23andMe reports both under an FDA authorization) but absent from others, and the broader tail of rare LRRK2/GBA/PRKN pathogenic variants is on no array.',
    sources: 'GWAS Catalog (EFO_0002508); Nalls et al. 2019',
    markers: [
      {
        rsid: 'rs356219',
        gene: 'SNCA',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect: 'Each G copy is associated with roughly 1.3× the odds of Parkinson\'s disease.',
      },
    ],
  },
  {
    id: 'ms',
    name: 'Multiple sclerosis',
    aliases: ['MS', 'demyelinating', 'HLA-DRB1', 'neurological autoimmune'],
    category: 'autoimmune',
    impact: 'modest',
    summary:
      'MS risk is dominated by HLA-DRB1*15:01. The SNP below tags that haplotype imperfectly.',
    sources: 'GWAS Catalog (EFO_0003885); IMSGC',
    markers: [
      {
        rsid: 'rs3135388',
        gene: 'HLA-DRA / DRB1*15:01 tag',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Tags DRB1*15:01. Each A copy is associated with roughly 3× the odds of MS — but MS is rare, so absolute risk stays low.',
      },
    ],
  },
  {
    id: 'hemochromatosis',
    name: 'Hereditary hemochromatosis (iron overload)',
    aliases: ['iron', 'HFE', 'C282Y', 'ferritin', 'iron overload'],
    category: 'metabolic',
    impact: 'clinical',
    summary:
      'One of the few genuinely actionable findings a consumer array can surface: iron overload is treatable, and treatment is more effective before organ damage.',
    caveat:
      'Penetrance is low — most C282Y homozygotes never develop clinical iron overload. This is a prompt to check serum ferritin and transferrin saturation with a doctor, not a diagnosis. Array calls also need clinical-grade confirmation before being acted on.',
    sources: 'ClinVar (HFE); EASL clinical practice guidelines',
    markers: [
      {
        rsid: 'rs1800562',
        gene: 'HFE',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'C282Y, the main hemochromatosis variant. Two copies are the classic high-risk genotype; one copy alone rarely causes overload.',
      },
      {
        rsid: 'rs1799945',
        gene: 'HFE',
        alleles: ['C', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'H63D, a milder variant. Meaningful mainly in combination with C282Y (compound heterozygous), and even then usually mild.',
      },
    ],
  },
  {
    id: 'factor-v-leiden',
    name: 'Factor V Leiden thrombophilia',
    aliases: ['blood clot', 'thrombosis', 'DVT', 'clotting', 'F5', 'pulmonary embolism'],
    category: 'hematologic',
    impact: 'clinical',
    summary:
      'The most common inherited clotting-risk variant in European-ancestry populations.',
    caveat:
      'Relevant to decisions about surgery, immobility, pregnancy, and estrogen-containing contraceptives — which is exactly why it warrants a clinical-grade test and a doctor\'s interpretation rather than an array readout.',
    sources: 'ClinVar (F5 p.Arg506Gln); ACMG guidance',
    markers: [
      {
        rsid: 'rs6025',
        gene: 'F5',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'Arg506Gln. One copy is associated with roughly 3–8× the risk of venous thromboembolism; two copies with substantially more.',
      },
    ],
  },
  {
    id: 'prothrombin',
    name: 'Prothrombin G20210A thrombophilia',
    aliases: ['blood clot', 'thrombosis', 'F2', 'prothrombin', 'clotting'],
    category: 'hematologic',
    impact: 'clinical',
    summary: 'The second most common inherited venous-clotting variant.',
    caveat:
      'As with Factor V Leiden — clinically relevant, and therefore worth a proper diagnostic test rather than an array call.',
    sources: 'ClinVar (F2 c.*97G>A)',
    markers: [
      {
        rsid: 'rs1799963',
        gene: 'F2',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Raises circulating prothrombin. One copy is associated with roughly 2–3× the risk of venous thromboembolism.',
      },
    ],
  },
  {
    id: 'sickle',
    name: 'Sickle cell (carrier status)',
    aliases: ['HBB', 'sickle cell trait', 'anemia', 'hemoglobin'],
    category: 'hematologic',
    impact: 'clinical',
    summary:
      'A single well-defined change in the beta-globin gene. Two copies cause sickle cell disease; one copy is sickle cell trait, usually asymptomatic but relevant to reproductive planning.',
    caveat:
      'rs334 is a palindromic A/T SNP, so strand orientation cannot be resolved from the call alone — see the strand warning on the result. Carrier status should be confirmed by hemoglobin electrophoresis or clinical sequencing.',
    sources: 'ClinVar (HBB p.Glu6Val)',
    markers: [
      {
        // dbSNP forward strand: T is the reference (normal Glu), A is the HbS
        // change. HBB is transcribed from the minus strand, so the coding
        // c.20A>T reads as forward T>A — an easy and dangerous one to invert.
        rsid: 'rs334',
        gene: 'HBB',
        alleles: ['T', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Glu6Val, historically p.Glu6Val and now numbered p.Glu7Val in ClinVar (HbS). One copy is sickle cell trait; two copies cause sickle cell anemia.',
      },
    ],
  },
  {
    id: 'lactose',
    name: 'Lactose intolerance (adult)',
    aliases: ['lactase', 'milk', 'dairy', 'MCM6', 'LCT'],
    category: 'trait',
    impact: 'trait',
    summary:
      'Most adults worldwide stop producing lactase after childhood; continued production is the derived state, common in populations with a dairying history.',
    caveat:
      'This marker predicts European-type lactase persistence. Several independent African and Middle Eastern persistence variants exist and are not captured here, so a "non-persistent" call is unreliable outside European ancestry.',
    sources: 'Enattah et al. 2002; MONDO_0100345 (lactose intolerance)',
    markers: [
      {
        rsid: 'rs4988235',
        gene: 'MCM6 (regulates LCT)',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'protective',
        effect:
          'The A allele keeps lactase switched on into adulthood. One copy is generally enough; G/G is associated with adult lactase non-persistence.',
      },
    ],
  },
  {
    id: 'gout',
    name: 'Gout / high uric acid',
    aliases: ['uric acid', 'urate', 'ABCG2', 'joint pain', 'hyperuricemia'],
    category: 'metabolic',
    impact: 'notable',
    summary:
      'ABCG2 is a urate transporter; the Q141K variant reduces urate excretion and is one of the largest common-variant effects on serum urate.',
    sources: 'GWAS Catalog (EFO_0004274); Köttgen et al. 2013',
    markers: [
      {
        rsid: 'rs2231142',
        gene: 'ABCG2',
        alleles: ['G', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'Q141K. Each T copy raises serum urate and is associated with roughly 1.7× the odds of gout.',
      },
    ],
  },
  {
    id: 'statin-myopathy',
    name: 'Statin-induced muscle pain',
    aliases: ['statin', 'simvastatin', 'myopathy', 'SLCO1B1', 'cholesterol drug', 'muscle pain'],
    category: 'pharmacogenomic',
    impact: 'notable',
    summary:
      'SLCO1B1 controls how quickly the liver clears statins. Reduced function raises blood levels and the chance of muscle side effects — strongest for simvastatin.',
    caveat:
      'Never change or stop a prescribed medication based on this. It is a conversation-starter with a prescriber, who can switch statin or adjust dose.',
    sources: 'CPIC guideline for SLCO1B1 and statins; PharmGKB',
    markers: [
      {
        rsid: 'rs4149056',
        gene: 'SLCO1B1',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'The c.521T>C decreased-function allele (carried on the *5/*15/*16/*17 haplotypes). In the trial that found it, one C copy carried roughly 4× the odds of myopathy on high-dose (80 mg) simvastatin; the effect is smaller at 40 mg and smaller still for other statins. Absolute risk is low either way.',
      },
    ],
  },
  {
    id: 'clopidogrel',
    name: 'Clopidogrel (Plavix) response',
    aliases: ['clopidogrel', 'Plavix', 'CYP2C19', 'antiplatelet', 'blood thinner'],
    category: 'pharmacogenomic',
    impact: 'notable',
    summary:
      'Clopidogrel is a prodrug that CYP2C19 must activate. Loss-of-function carriers get less antiplatelet effect from a standard dose.',
    caveat:
      'Only the common *2 allele is checked here; *3 and other loss-of-function alleles (more frequent in East Asian populations) are not. Prescribing decisions belong with a clinician.',
    sources: 'CPIC guideline for CYP2C19 and clopidogrel; PharmGKB',
    markers: [
      {
        rsid: 'rs4244285',
        gene: 'CYP2C19',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'The *2 loss-of-function allele. Carriers activate clopidogrel less efficiently; two copies means poor metabolizer status.',
      },
    ],
  },
  {
    id: 'warfarin',
    name: 'Warfarin dose sensitivity',
    aliases: ['warfarin', 'Coumadin', 'VKORC1', 'CYP2C9', 'anticoagulant', 'blood thinner'],
    category: 'pharmacogenomic',
    impact: 'notable',
    summary:
      'Warfarin dosing varies more than tenfold between people, and these two genes explain a large share of it — one of the best-validated pharmacogenomic findings.',
    caveat:
      'Warfarin dosing is always managed by INR monitoring. Genotype refines a starting dose; it never replaces the blood test. The three variants here explain a large share of dose variation in European and East Asian ancestry — in African ancestry, CYP2C9*5, *6, *8, *11 and rs12777823 drive much of the effect and none of them are checked here.',
    sources: 'CPIC guideline for warfarin dosing; PharmGKB',
    markers: [
      {
        rsid: 'rs9923231',
        gene: 'VKORC1',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'Reduces the amount of warfarin target enzyme. T carriers typically need lower doses; T/T markedly lower.',
      },
      {
        rsid: 'rs1799853',
        gene: 'CYP2C9',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect: 'The *2 allele — slower warfarin clearance, so a lower dose requirement.',
      },
      {
        rsid: 'rs1057910',
        gene: 'CYP2C9',
        alleles: ['A', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect: 'The *3 allele — a larger reduction in clearance than *2.',
      },
    ],
  },
  {
    id: 'alcohol-flush',
    name: 'Alcohol flush reaction',
    aliases: ['alcohol', 'ALDH2', 'asian flush', 'drinking', 'acetaldehyde'],
    category: 'metabolic',
    impact: 'notable',
    summary:
      'ALDH2 clears acetaldehyde, the toxic intermediate of alcohol metabolism. The deficient allele causes flushing, nausea, and a raised esophageal-cancer risk in people who drink despite it.',
    caveat:
      'Common in East Asian populations and nearly absent elsewhere. The cancer-risk element is the medically meaningful part, not the flushing.',
    sources: 'ClinVar (ALDH2 p.Glu504Lys); Brooks et al. 2009',
    markers: [
      {
        rsid: 'rs671',
        gene: 'ALDH2',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'The *2 allele. One copy cuts enzyme activity sharply and causes flushing; two copies leave almost none.',
      },
    ],
  },
  {
    id: 'nicotine',
    name: 'Nicotine dependence',
    aliases: ['smoking', 'nicotine', 'CHRNA5', 'cigarettes', 'tobacco'],
    category: 'trait',
    impact: 'modest',
    summary:
      'A nicotinic receptor variant associated with heavier smoking among smokers, and indirectly with lung cancer and COPD risk through that.',
    sources: 'GWAS Catalog (EFO_0003768); TAG consortium',
    markers: [
      {
        rsid: 'rs16969968',
        gene: 'CHRNA5',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'D398N. Each A copy is associated with roughly one extra cigarette per day among smokers, and greater difficulty quitting.',
      },
    ],
  },
  {
    id: 'breast-cancer',
    name: 'Breast cancer (common variants)',
    aliases: ['cancer', 'FGFR2', 'BRCA', 'oncology'],
    category: 'cancer',
    impact: 'modest',
    summary:
      'Common variants each shift breast-cancer risk slightly. They are unrelated to the high-penetrance BRCA1/BRCA2 mutations.',
    caveat:
      'This does NOT screen for BRCA1/BRCA2. Those are mostly rare mutations spread across two large genes, which arrays do not cover; a negative result here says nothing about them. If you have a family history, ask about clinical BRCA testing.',
    sources: 'GWAS Catalog (EFO_0000305); BCAC',
    markers: [
      {
        rsid: 'rs2981582',
        gene: 'FGFR2',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'One of the earliest replicated breast-cancer loci. Each A copy is associated with roughly 1.26× the odds.',
      },
    ],
  },
  {
    id: 'prostate-cancer',
    name: 'Prostate cancer (common variants)',
    aliases: ['cancer', '8q24', 'prostate', 'oncology'],
    category: 'cancer',
    impact: 'modest',
    summary:
      'The 8q24 gene desert holds several independent prostate-cancer signals — an early and durable GWAS finding.',
    sources: 'GWAS Catalog (EFO_0001663); PRACTICAL consortium',
    markers: [
      {
        rsid: 'rs1447295',
        gene: '8q24',
        alleles: ['C', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect: 'Each A copy is associated with roughly 1.4× the odds of prostate cancer.',
      },
    ],
  },
  {
    id: 'caffeine',
    name: 'Caffeine metabolism',
    aliases: ['coffee', 'caffeine', 'CYP1A2', 'espresso'],
    category: 'trait',
    impact: 'trait',
    summary:
      'CYP1A2 clears most caffeine. The slow-metabolizer allele is associated with caffeine lingering longer.',
    caveat:
      'Widely reported but less consistently replicated than the medical entries here. Treat it as interesting rather than settled.',
    sources: 'Cornelis et al. 2006; PharmGKB (CYP1A2)',
    markers: [
      {
        rsid: 'rs762551',
        gene: 'CYP1A2',
        alleles: ['A', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'rs762551 −163C>A. The A allele (CYP1A2*1F, renamed *30 by PharmVar) is the high-inducibility, fast-metabolizer form; C carriers clear caffeine more slowly, and A/A is the fast-metabolizer genotype.',
      },
    ],
  },
  {
    id: 'muscle-fiber',
    name: 'Muscle fiber type (sprint vs endurance)',
    aliases: ['ACTN3', 'sprint', 'athletic', 'exercise', 'fast twitch'],
    category: 'trait',
    impact: 'trait',
    summary:
      'ACTN3 is expressed in fast-twitch muscle fibers. The X (stop-codon) allele removes the protein — common, harmless, and slightly over-represented in endurance athletes.',
    caveat:
      'Popular but weak: the effect on actual athletic performance is small and inconsistent across studies. Not a talent test.',
    sources: 'Yang et al. 2003; Ma et al. 2013 meta-analysis',
    markers: [
      {
        rsid: 'rs1815739',
        gene: 'ACTN3',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'trait',
        effect:
          'R577X. T is the stop-codon (X) allele; T/T produces no ACTN3 protein, which is associated with endurance rather than sprint profiles.',
      },
    ],
  },
  {
    id: 'earwax',
    name: 'Earwax type & body odor',
    aliases: ['ABCC11', 'earwax', 'cerumen', 'body odour', 'sweat'],
    category: 'trait',
    impact: 'trait',
    summary:
      'A single variant determines wet versus dry earwax, and correlates with underarm odor — one of the cleanest single-gene traits in humans.',
    sources: 'Yoshiura et al. 2006',
    markers: [
      {
        rsid: 'rs17822931',
        gene: 'ABCC11',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'protective',
        effect:
          'T/T gives dry earwax and markedly reduced underarm odor. Any C copy gives wet earwax. Dry-type is common in East Asian populations.',
      },
    ],
  },
  {
    id: 'secretor',
    name: 'FUT2 secretor status (norovirus resistance)',
    aliases: ['FUT2', 'norovirus', 'secretor', 'B12', 'winter vomiting'],
    category: 'trait',
    impact: 'trait',
    summary:
      'Non-secretors do not display certain blood-group antigens in mucus, which makes them resistant to the dominant strains of norovirus.',
    sources: 'Lindesmith et al. 2003; GWAS Catalog (vitamin B12 measurement, EFO_0004620)',
    markers: [
      {
        rsid: 'rs601338',
        gene: 'FUT2',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'protective',
        effect:
          'A/A is non-secretor: strong resistance to Norwalk-type norovirus, and slightly higher vitamin B12 levels.',
      },
    ],
  },
  {
    id: 'ulcerative-colitis',
    name: 'Ulcerative colitis',
    aliases: ['UC', 'IBD', 'inflammatory bowel disease', 'colitis', 'bloody diarrhea', 'gut inflammation'],
    category: 'autoimmune',
    impact: 'notable',
    summary:
      "Ulcerative colitis shares most of its genetic architecture with Crohn's disease, but effect sizes differ and a few loci point in opposite directions. The three markers below are among the most consistently replicated; together they account for only a small share of risk.",
    caveat:
      'Diagnosis is made by colonoscopy and biopsy, never by genotype. The HLA marker here is a rough tag for HLA-DQ haplotypes rather than an HLA type, and it is thinly covered on some consumer arrays.',
    sources: 'GWAS Catalog (EFO_0000729); Anderson et al. 2011; Jostins et al. 2012; de Lange et al. 2017; FinnGen R12',
    markers: [
      {
        rsid: 'rs11209026',
        gene: 'IL23R',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'protective',
        effect:
          "R381Q. Each A copy is associated with roughly 0.6× the odds of ulcerative colitis (about 0.68× in Finnish biobank data) — the same protective variant seen in Crohn's disease, where the effect is larger.",
      },
      {
        rsid: 'rs3024505',
        gene: 'IL10',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Sits just downstream of IL10, an anti-inflammatory signal. Each A copy is associated with roughly 1.25× the odds of ulcerative colitis. The A allele is common in Europeans (~17%) and uncommon in East Asian and African-ancestry groups (~3%).',
      },
      {
        rsid: 'rs6927022',
        gene: 'HLA-DQA1 region',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Near HLA-DQA1, the strongest ulcerative colitis region. Reported at about 1.44× per A copy in the 2011 European meta-analysis and about 1.18× in Finnish biobank data. The same G allele that is protective here is a large type 1 diabetes risk allele, because the two diseases track different HLA haplotypes.',
      },
    ],
  },
  {
    id: 'lupus',
    name: 'Systemic lupus erythematosus',
    aliases: ['SLE', 'lupus', 'butterfly rash', 'ANA', 'autoimmune', 'connective tissue disease'],
    category: 'autoimmune',
    impact: 'notable',
    summary:
      'Lupus has some of the largest common-variant effects in autoimmunity outside the HLA region, and the three markers below are among the best replicated. They still explain only a fraction of who develops the disease.',
    caveat:
      'Lupus is roughly nine times more common in women and more common and more severe in people of African, Hispanic, and Asian ancestry — none of which is captured by these markers. Diagnosis is clinical, supported by ANA and anti-dsDNA testing.',
    sources: 'GWAS Catalog (EFO_0002690); Nath et al. 2008 (ITGAM); Bentham et al. 2015; Graham et al. 2007 (IRF5)',
    markers: [
      {
        rsid: 'rs7574865',
        gene: 'STAT4',
        alleles: ['T', 'G'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'Each T copy is associated with roughly 1.5× the odds of lupus, and with earlier and more severe disease in some cohorts. Replicated in European, East Asian, and Hispanic groups; T is much rarer in African-ancestry populations (~12%).',
      },
      {
        rsid: 'rs1143679',
        gene: 'ITGAM',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'R77H, in a leukocyte adhesion receptor. Each A copy is associated with roughly 1.8× the odds of lupus in European- and African-ancestry cohorts. The allele is rare in East Asians (~2%), where this locus contributes little.',
      },
      {
        rsid: 'rs10488631',
        gene: 'IRF5-TNPO3',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'Marks the IRF5 risk haplotype, which raises type I interferon signalling. Each C copy is associated with roughly 1.8-1.9× the odds of lupus in Europeans (the C allele is at ~10%, so about 19% carry a copy); the allele is very rare in East Asian and African-ancestry populations.',
      },
    ],
  },
  {
    id: 'graves-disease',
    name: "Graves' disease (autoimmune hyperthyroidism)",
    aliases: ['Graves', 'overactive thyroid', 'hyperthyroidism', 'thyrotoxicosis', 'TSHR', 'thyroid eye disease'],
    category: 'autoimmune',
    impact: 'modest',
    summary:
      "Antibodies that switch on the TSH receptor drive Graves' disease. Genetics contributes substantially, but the largest share sits in HLA class II, which consumer arrays cover poorly. The three markers below are the best-replicated non-HLA signals.",
    caveat:
      "Graves' is diagnosed with thyroid function tests and TSH-receptor antibodies, and it is treatable — genotype adds nothing to that. HLA class II, the biggest genetic contributor, is missing here.",
    sources: 'GWAS Catalog (MONDO_0005364); Ueda et al. 2003 (CTLA4 CT60); Chu et al. 2011 and Xiong et al. 2016 meta-analysis (TSHR rs179247); FinnGen R12',
    markers: [
      {
        rsid: 'rs179247',
        gene: 'TSHR',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          "In intron 1 of the TSH receptor — the exact antigen Graves' antibodies target. A meta-analysis across Chinese, Japanese, Polish, UK and Brazilian cohorts found about 1.40× per A copy (A/A vs G/G about 1.9×); Finnish biobank data give about 1.29×.",
      },
      {
        rsid: 'rs3087243',
        gene: 'CTLA4',
        alleles: ['G', 'A'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          "The CT60 variant, near a brake on T-cell activation. Each G copy is associated with roughly 1.3× the odds of Graves' disease; the A allele is correspondingly protective here and across several other autoimmune conditions.",
      },
      {
        rsid: 'rs2476601',
        gene: 'PTPN22',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          "R620W, the broad autoimmunity variant. Each A copy is associated with roughly 1.3× the odds of Graves' disease in European-ancestry cohorts. The allele is essentially absent in East Asian populations.",
      },
    ],
  },
  {
    id: 'hashimoto-thyroiditis',
    name: "Hashimoto's thyroiditis (autoimmune hypothyroidism)",
    aliases: ['Hashimoto', 'underactive thyroid', 'hypothyroidism', 'TPO antibodies', 'thyroiditis', 'levothyroxine'],
    category: 'autoimmune',
    impact: 'modest',
    summary:
      "The commonest autoimmune disease in iodine-replete countries, and the commonest cause of an underactive thyroid. The markers below come from very large biobank analyses of autoimmune hypothyroidism; each shifts odds by a modest amount and they overlap heavily with the Graves' loci.",
    caveat:
      'Most hypothyroidism is diagnosed and managed on TSH and thyroid-antibody blood tests, which are cheap and definitive — genotype adds nothing clinically. Many cases of an underactive thyroid are not autoimmune at all.',
    sources: 'GWAS Catalog (EFO_0004705, hypothyroidism); Kichaev et al. 2019; FinnGen R12 (autoimmune hypothyroidism, ~55,000 cases)',
    markers: [
      {
        rsid: 'rs2476601',
        gene: 'PTPN22',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'R620W. Each A copy is associated with roughly 1.35× the odds of autoimmune hypothyroidism in European-ancestry data. The allele is essentially absent in East Asian populations, so it explains none of the risk there.',
      },
      {
        rsid: 'rs3087243',
        gene: 'CTLA4',
        alleles: ['G', 'A'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'The CT60 variant. Each G copy is associated with roughly 1.13-1.16× the odds of autoimmune hypothyroidism — small, but among the most consistently replicated non-HLA thyroid signals.',
      },
      {
        rsid: 'rs3184504',
        gene: 'SH2B3',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'R262W, a shared autoimmunity locus that also appears in celiac disease and type 1 diabetes. Each T copy is associated with roughly 1.17× the odds. T is common in Europeans (~46%) and nearly absent in East Asian and African-ancestry populations.',
      },
    ],
  },
  {
    id: 'ankylosing-spondylitis',
    name: 'Ankylosing spondylitis',
    aliases: ['AS', 'axial spondyloarthritis', 'HLA-B27', 'spine inflammation', 'back pain', 'sacroiliitis'],
    category: 'autoimmune',
    impact: 'notable',
    summary:
      'One of the most strongly genetic common diseases: HLA-B*27 accounts for a large share of risk, and ERAP1 modifies it almost exclusively in B*27-positive people. The first marker below is a tag for B*27, not the HLA type itself.',
    caveat:
      'rs4349859 tags HLA-B*27 in European-ancestry populations only — it is near-absent in East Asian, South Asian, and African groups where B*27 sits on different haplotypes, so a negative result there means nothing. Even among true B*27 carriers, only about 5% develop ankylosing spondylitis. Proper B*27 typing plus imaging is the real assessment.',
    sources: 'GWAS Catalog (MONDO_0005306); Evans et al. 2011 (TASC/WTCCC2); Cortes et al. 2013 (IGAS); FinnGen R12',
    markers: [
      {
        rsid: 'rs4349859',
        gene: 'HLA-B (B*27 tag)',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Tags HLA-B*27. In Finnish biobank data each A copy carries roughly 6× the odds of ankylosing spondylitis; HLA-B*27 itself, typed directly, carries odds ratios in the tens. The A allele is carried by about 5% of Europeans and under 1% of most other populations.',
      },
      {
        rsid: 'rs30187',
        gene: 'ERAP1',
        alleles: ['T', 'C'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'K528R, in the enzyme that trims peptides for HLA-B*27 to display. Each T (Lys528) copy is associated with roughly 1.2-1.3× the odds; the C (Arg528) allele lowers enzyme activity and is protective. The effect is seen almost only in HLA-B*27-positive people.',
      },
      {
        rsid: 'rs11209026',
        gene: 'IL23R',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'protective',
        effect:
          "R381Q. Each A copy is associated with roughly 0.7× the odds of ankylosing spondylitis — the same variant that protects against Crohn's disease and ulcerative colitis, which run in the same families.",
      },
    ],
  },
  {
    id: 'sarcoidosis',
    name: 'Sarcoidosis',
    aliases: ['sarcoid', 'granuloma', 'Löfgren syndrome', 'lung inflammation', 'BTNL2', 'hilar lymphadenopathy'],
    category: 'autoimmune',
    impact: 'notable',
    summary:
      'An inflammatory disease that forms granulomas, most often in the lungs and lymph nodes. Two of the markers below are among the largest and best-replicated common-variant effects in sarcoidosis; the third is a general autoimmunity locus.',
    caveat:
      'Sarcoidosis is roughly three times more common, and more often chronic, in people of African ancestry — yet most genetic work has been done in Europeans, so these estimates travel poorly. BTNL2 sits inside the MHC next to HLA-DRB1, and part of its signal may reflect nearby HLA types rather than BTNL2 itself.',
    sources: 'GWAS Catalog (MONDO_0019338); Valentonyte et al. 2005 (BTNL2); Hofmann et al. 2008 (ANXA11); Tian et al. 2015 meta-analysis; FinnGen R12',
    markers: [
      {
        rsid: 'rs2076530',
        gene: 'BTNL2',
        alleles: ['T', 'C'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'A splice-site change that truncates BTNL2, an immune co-regulator. Pooled across ten studies the risk allele carries about 1.6× the odds per copy (about 1.4× in Finnish biobank data). Note that the human reference genome happens to carry the risk allele at this position.',
      },
      {
        rsid: 'rs1049550',
        gene: 'ANXA11',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'protective',
        effect:
          'R230C. Each A (Cys230) copy is associated with roughly 0.78× the odds of sarcoidosis — one of the few sarcoidosis findings replicated in both European and African-American cohorts.',
      },
      {
        rsid: 'rs3184504',
        gene: 'SH2B3',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'R262W, a shared autoimmunity locus. Each T copy is associated with roughly 1.17× the odds of sarcoidosis. T is common in Europeans (~46%) and nearly absent in East Asian and African-ancestry populations.',
      },
    ],
  },
  {
    id: 'sjogrens-syndrome',
    name: "Sjögren's syndrome",
    aliases: ['Sjogren', 'sicca syndrome', 'dry eyes', 'dry mouth', 'anti-Ro', 'SSA antibodies'],
    category: 'autoimmune',
    impact: 'modest',
    summary:
      'Immune attack on the tear and saliva glands. The genetics closely mirrors lupus: HLA class II dominates, and the same two interferon-pathway loci show up outside it. Both markers below shift odds by modest amounts.',
    caveat:
      "About nine in ten patients are women, and dryness of the eyes and mouth is very common for reasons that have nothing to do with Sjögren's. Diagnosis rests on anti-Ro/SSA antibodies and sometimes a lip-gland biopsy. The largest genetic contributor, HLA class II, is not captured here.",
    sources: 'GWAS Catalog (MONDO_0010030); Lessard et al. 2013; Nordmark et al. 2009 (IRF5); FinnGen R12',
    markers: [
      {
        rsid: 'rs7574865',
        gene: 'STAT4',
        alleles: ['T', 'G'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          "Each T copy is associated with roughly 1.2-1.5× the odds of Sjögren's syndrome across European and East Asian cohorts (about 1.21× in Finnish biobank data).",
      },
      {
        rsid: 'rs10488631',
        gene: 'IRF5-TNPO3',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          "Marks the IRF5 interferon-signalling risk haplotype. Each C copy is associated with roughly 1.3-1.4× the odds of Sjögren's syndrome. The allele reaches about 10% in Europeans and is very rare in East Asian and African-ancestry populations.",
      },
    ],
  },
  {
    id: 'primary-biliary-cholangitis',
    name: 'Primary biliary cholangitis',
    aliases: ['PBC', 'primary biliary cirrhosis', 'AMA', 'bile duct', 'liver autoimmune', 'itching'],
    category: 'autoimmune',
    impact: 'modest',
    summary:
      'An autoimmune disease of the small bile ducts. Its genetics is unusually focused on the interleukin-12 pathway, and the loci below were among the first non-HLA findings, replicated across independent European-ancestry cohorts. Those cohorts are the caveat: PBC is one of the clearest cases of a disease whose risk loci do not travel between ancestries — Japanese and Han Chinese studies found no association at IL12A or IL12RB2, and every effect size quoted below is a European-ancestry figure.',
    caveat:
      'About nine in ten patients are women, and the disease is uncommon — so even a doubling of odds leaves absolute risk low. Diagnosis is by anti-mitochondrial antibodies and liver enzymes, and effective treatment exists; none of that is informed by genotype.',
    sources: 'GWAS Catalog (MONDO_0005388); Hirschfield et al. 2009 (IL12A, IL12RB2); Mells et al. 2011; Cordell et al. 2015; FinnGen R12',
    markers: [
      {
        rsid: 'rs3790567',
        gene: 'IL12RB2',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'In the receptor for interleukin-12. Each A copy is associated with roughly 1.4-1.5× the odds of primary biliary cholangitis, replicated across independent European cohorts. A is at about 24% in Europeans, so roughly 43% carry at least one copy.',
      },
      {
        rsid: 'rs485499',
        gene: 'IL12A',
        alleles: ['T', 'C'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'About 32 kb downstream of IL12A, the gene for the interleukin-12 p35 subunit, inside an intron of the IL12A-AS1 antisense transcript. Each T copy is associated with roughly 1.3-1.4× the odds of primary biliary cholangitis.',
      },
      {
        rsid: 'rs10488631',
        gene: 'IRF5-TNPO3',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          "The IRF5 risk haplotype, shared with lupus and Sjögren's syndrome. Each C copy is associated with roughly 1.4-1.6× the odds of primary biliary cholangitis in European-ancestry cohorts.",
      },
    ],
  },
  {
    id: 'hypertension',
    name: 'High blood pressure',
    aliases: ['hypertension', 'blood pressure', 'systolic', 'diastolic', 'ATP2B1', 'CSK'],
    category: 'cardiovascular',
    impact: 'modest',
    summary:
      'Blood pressure is one of the most polygenic traits known — over a thousand loci contribute, and each shifts the average by a fraction of a millimetre of mercury. The two below are among the earliest and most consistently replicated, but they are drops in a very large bucket.',
    caveat:
      'A cuff reading is vastly more informative than any genotype here. Combined, these two variants move average systolic pressure by under 2 mmHg — less than the difference between sitting still and having just climbed the stairs.',
    sources: 'GWAS Catalog (EFO_0000537; systolic and diastolic blood pressure); Newton-Cheh et al. 2009 (Global BPgen); Evangelou et al. 2018 (ICBP + UK Biobank)',
    markers: [
      {
        rsid: 'rs17249754',
        gene: 'ATP2B1',
        alleles: ['G', 'A'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'Near a calcium-pump gene expressed in vascular smooth muscle. Each G copy is associated with roughly +1.0 mmHg systolic and +0.6 mmHg diastolic pressure on average. G is the common allele everywhere it has been measured, but how common varies by ancestry — around 85% in European-ancestry cohorts and closer to 65-69% in East and South Asian groups.',
      },
      {
        rsid: 'rs1378942',
        gene: 'CSK',
        alleles: ['C', 'A'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'In the CSK/CYP1A1 region on 15q24. Each C copy is associated with roughly +0.6 mmHg systolic and +0.4 mmHg diastolic pressure. Allele frequencies differ sharply by ancestry — C is the minor allele in Europeans (~35%) and much more common in African-ancestry groups.',
      },
    ],
  },
  {
    id: 'high-ldl',
    name: 'High LDL cholesterol',
    aliases: ['LDL', 'bad cholesterol', 'hyperlipidemia', 'APOB', 'LDLR', 'SORT1', 'statins'],
    category: 'cardiovascular',
    impact: 'modest',
    summary:
      "Common variants nudge lifelong average LDL up or down by a few mg/dL each. These three sit in the genes that actually handle LDL — the particle's main protein, its receptor, and a liver sorting protein — and are among the strongest common signals in the global lipid meta-analyses.",
    caveat:
      'This is not familial hypercholesterolemia. FH is caused by rare, largely private mutations in LDLR, APOB and PCSK9. Some consumer platforms screen a fixed handful — 23andMe reports 23 LDLR variants and one in APOB under an FDA authorization — but that is a tiny slice of the many hundreds of known FH mutations, and nothing at all in PCSK9, so a quiet result there does not rule FH out, and it can raise LDL two- to threefold. A lipid panel is the measurement that matters; if LDL runs high in the family, that is a clinical-sequencing question, not an array question.',
    sources: 'GWAS Catalog (EFO_0004611, low density lipoprotein cholesterol measurement); Willer et al. 2013 (GLGC); Graham et al. 2021 (GLGC multi-ancestry)',
    markers: [
      {
        rsid: 'rs693',
        gene: 'APOB',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          "The old XbaI polymorphism, in the gene for the structural protein of LDL. Each A copy is associated with roughly +0.06 g/L apolipoprotein B and about a tenth of a standard deviation higher LDL. Close to a coin-flip in frequency (A ≈ 48%). Older papers call this the 'T' allele because APOB is read on the opposite strand.",
      },
      {
        rsid: 'rs6511720',
        gene: 'LDLR',
        alleles: ['G', 'T'],
        effectAllele: 'T',
        direction: 'protective',
        effect:
          'In the LDL receptor gene itself. Each T copy is associated with roughly 7 mg/dL lower LDL, and with less coronary artery calcification. T is uncommon (~10% in European-ancestry cohorts).',
      },
      {
        rsid: 'rs12740374',
        gene: 'SORT1 / CELSR2 (1p13)',
        alleles: ['G', 'T'],
        effectAllele: 'T',
        direction: 'protective',
        effect:
          'The functional variant at the 1p13 locus — the T allele creates a transcription-factor binding site that raises liver SORT1 expression. Each T copy is associated with roughly 5–6 mg/dL lower LDL. T frequency is about 21% in Europeans and similar in African-ancestry groups (~25%), but much rarer in East Asians (~4%).',
      },
    ],
  },
  {
    id: 'high-triglycerides',
    name: 'High triglycerides',
    aliases: ['triglycerides', 'TG', 'hypertriglyceridemia', 'APOA5', 'GCKR', 'LPL', 'blood fats'],
    category: 'metabolic',
    impact: 'notable',
    summary:
      'Triglycerides are the most genetically tractable of the standard lipids — a handful of common variants move them by amounts a lab report would notice. The APOA5 signal below is one of the largest common-variant effects on any blood lipid.',
    caveat:
      'Triglycerides swing enormously with the last meal, alcohol, and weight — far more than genotype does. A fasting lipid panel is the real measurement. Very high triglycerides (above ~500 mg/dL) are usually multifactorial — polygenic burden on top of alcohol, weight, uncontrolled diabetes, or medication. Under ~5% are monogenic familial chylomicronemia, from biallelic rare variants in LPL, APOC2, GPIHBP1 or LMF1, which arrays do not cover.',
    sources: 'GWAS Catalog (EFO_0004530, triglyceride measurement); Teslovich et al. 2010 and Willer et al. 2013 (GLGC)',
    markers: [
      {
        rsid: 'rs964184',
        gene: 'APOA5 / ZPR1 (11q23)',
        alleles: ['G', 'C'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'The strongest common triglyceride variant. Each G copy is associated with roughly +17 mg/dL triglycerides, plus modestly lower HDL and ~1.13× the odds of coronary artery disease. G is the minor allele (~13% in Europeans, ~20% globally).',
      },
      {
        rsid: 'rs1260326',
        gene: 'GCKR',
        alleles: ['T', 'C'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'P446L, in the glucokinase regulatory protein. Each T copy is associated with higher triglycerides and higher C-reactive protein, and — unusually — with slightly lower fasting glucose. T frequency is about 39%.',
      },
      {
        rsid: 'rs328',
        gene: 'LPL',
        alleles: ['C', 'G'],
        effectAllele: 'G',
        direction: 'protective',
        effect:
          'S447X — a premature stop that paradoxically makes lipoprotein lipase work better. Each G copy is associated with roughly 6 mg/dL lower triglycerides, higher HDL, and about 0.93× the odds of coronary artery disease. G frequency is about 10%.',
      },
    ],
  },
  {
    id: 'lipoprotein-a',
    name: 'Elevated lipoprotein(a)',
    aliases: ['Lp(a)', 'lipoprotein little a', 'LPA', 'apo(a)', 'cholesterol', 'heart risk'],
    category: 'cardiovascular',
    impact: 'notable',
    summary:
      'Lp(a) is an LDL-like particle whose blood level is set almost entirely by the LPA gene and barely moves with diet, exercise, or statins. These two variants together explain a large share of the variation in European-ancestry populations, and both track directly onto coronary disease and aortic valve calcification.',
    caveat:
      "Two things this cannot do. First, most of the heritability of Lp(a) sits in a variable-length repeat (the KIV-2 copy number) that no genotyping array measures — so a 'clear' result here does not mean a normal Lp(a). Second, rs10455872 is essentially a European-ancestry marker: it is rare in East Asian and African-ancestry groups, where it explains much less. Lp(a) is a cheap blood test that most guidelines now suggest measuring once in a lifetime. Measure it rather than infer it.",
    sources: 'GWAS Catalog (lipoprotein A measurement; coronary artery disease; aortic valve calcification); Clarke et al. 2009 NEJM; Thanassoulis et al. 2013 NEJM',
    markers: [
      {
        rsid: 'rs10455872',
        gene: 'LPA',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'The single largest common determinant of Lp(a) level, explaining roughly a quarter of its variance in European-ancestry populations. Each G copy was associated with about 1.70× the odds of coronary disease in the original PROCARDIS report and roughly 1.3× in larger later meta-analyses, plus ~1.4× the odds of aortic stenosis. G frequency is about 7% in Europeans and much lower elsewhere.',
      },
      {
        rsid: 'rs3798220',
        gene: 'LPA',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'I4399M in apolipoprotein(a). Each C copy raises Lp(a) substantially and is associated with roughly 1.5× the odds of coronary disease. C is rare in European-ancestry groups (~2%) but reaches around 15% in some Hispanic/Latino populations, so its contribution varies a lot by ancestry.',
      },
    ],
  },
  {
    id: 'ischemic-stroke',
    name: 'Ischemic stroke',
    aliases: ['stroke', 'brain attack', 'cerebral infarction', 'HDAC9', 'PITX2', 'clot'],
    category: 'cardiovascular',
    impact: 'modest',
    summary:
      'Ischemic stroke is not one disease, and its genetics reflect that: the strongest common variants are specific to a subtype. HDAC9 tracks large-artery atherosclerotic stroke; the 4q25 locus tracks stroke caused by clots thrown from a fibrillating atrium. Both are well replicated in the large stroke consortia.',
    caveat:
      'Effects are small and subtype-specific — neither variant predicts stroke overall with any useful accuracy. Blood pressure, atrial fibrillation, smoking, and diabetes dominate individual risk by a wide margin, and all four are modifiable or treatable. The 4q25 marker also appears in the atrial fibrillation entry; it is one signal counted in two places, not two independent findings.',
    sources: 'GWAS Catalog (EFO_0000712, stroke; large artery stroke; cardioembolic stroke); Bellenguez et al. 2012; Malik et al. 2018 (MEGASTROKE)',
    markers: [
      {
        rsid: 'rs2107595',
        gene: 'HDAC9',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'The clearest common signal for large-artery atherosclerotic stroke. Each A copy is associated with roughly 1.15–1.20× the odds of that subtype, and about 1.09× the odds of ischemic stroke of any kind. A frequency is roughly 16–23% depending on population.',
      },
      {
        rsid: 'rs2200733',
        gene: '4q25 (near PITX2)',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'The atrial fibrillation locus, acting on stroke through the heart rather than the vessel wall. Each T copy is associated with roughly 1.37× the odds of cardioembolic stroke and about 1.17× the odds of ischemic stroke overall. T frequency is about 11% in European-ancestry cohorts.',
      },
    ],
  },
  {
    id: 'aortic-aneurysm',
    name: 'Abdominal aortic aneurysm',
    aliases: ['AAA', 'aneurysm', 'aorta', 'DAB2IP', '9p21', 'aortic dilation'],
    category: 'cardiovascular',
    impact: 'modest',
    summary:
      "An abdominal aortic aneurysm is a weakening and ballooning of the body's main artery, usually silent until it is large. Two loci replicate well: DAB2IP on 9q33 and the same 9p21 region that drives coronary disease.",
    caveat:
      'Both effects are small, and smoking is by far the dominant risk factor — it outweighs anything on this page. Screening is by one-time abdominal ultrasound, offered in many countries to older men who have ever smoked; that recommendation is driven by age, sex, and smoking history, not genotype. The 9p21 marker is shared with the coronary artery disease entry.',
    sources: 'GWAS Catalog (abdominal aortic aneurysm; aneurysm); Gretarsdottir et al. 2010 Nat Genet; Helgadottir et al. 2008 Nat Genet',
    markers: [
      {
        rsid: 'rs7025486',
        gene: 'DAB2IP',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Discovered in an Icelandic/Dutch genome-wide scan and replicated in several cohorts. Each A copy is associated with roughly 1.21× the odds of abdominal aortic aneurysm, and more weakly with early myocardial infarction and peripheral arterial disease. A frequency is about 23–26%.',
      },
      {
        rsid: 'rs10757278',
        gene: 'CDKN2B-AS1 (9p21)',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'The coronary disease locus also associates with aneurysm. Each G copy is associated with roughly 1.3× the odds of abdominal aortic aneurysm. G is common (~45–50%).',
      },
    ],
  },
  {
    id: 'nafld',
    name: 'Fatty liver disease (NAFLD / MASLD)',
    aliases: ['fatty liver', 'NAFLD', 'MASLD', 'NASH', 'steatosis', 'PNPLA3', 'TM6SF2', 'liver fat'],
    category: 'metabolic',
    impact: 'notable',
    summary:
      'Fat accumulation in the liver has an unusually strong common-variant component for a complex trait. PNPLA3 I148M is the standout: it raises liver fat, liver enzymes, and the odds of progressing to fibrosis and cirrhosis, and it does so on top of — not instead of — alcohol and metabolic risk.',
    caveat:
      'Both variants raise liver fat but neither is a diagnosis; liver fat is assessed by imaging or elastography, with enzymes as a first hint. The PNPLA3 risk allele is much more common in Hispanic/Latino populations (~45–50%) than in European-ancestry (~23%) or African-ancestry (~12%) groups, and this frequency gap is a well-documented contributor to differing NAFLD rates between populations. TM6SF2 rs58542926 is not on every consumer array.',
    sources: 'GWAS Catalog (liver fat measurement; cirrhosis of liver; alcoholic liver cirrhosis); Romeo et al. 2008 Nat Genet; Kozlitina et al. 2014 Nat Genet; Buch et al. 2015',
    markers: [
      {
        rsid: 'rs738409',
        gene: 'PNPLA3',
        alleles: ['C', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'I148M (the G allele encodes methionine), which cripples fat mobilisation from liver lipid droplets. Each G copy raises hepatic fat and ALT; carriers show roughly 1.4× the odds of cirrhosis overall and around 2.2× the odds of alcohol-related cirrhosis. G frequency varies strongly by ancestry — highest in Hispanic/Latino populations.',
      },
      {
        rsid: 'rs58542926',
        gene: 'TM6SF2',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'E167K. Each T copy raises liver fat and is associated with roughly 1.45× the odds of cirrhosis — while simultaneously lowering LDL cholesterol and triglycerides, because the liver retains the fat instead of exporting it. T frequency is about 7% in European-ancestry cohorts.',
      },
    ],
  },
  {
    id: 'gallstones',
    name: 'Gallstones',
    aliases: ['gallstones', 'cholelithiasis', 'gallbladder', 'biliary', 'ABCG8', 'cholesterol stones'],
    category: 'metabolic',
    impact: 'notable',
    summary:
      'Most gallstones are cholesterol stones, and the sterol transporter ABCG8 is the dominant common-variant locus. The D19H variant is one of the larger common-variant effects on any common disease — it pushes more cholesterol into bile, where it crystallises.',
    caveat:
      'D19H is close to absent in East Asian populations (~1%), so this entry is uninformative there — but it is carried at roughly European frequency in African-ancestry groups (~5-8%) and is somewhat more common in admixed American groups (~7-10%), so it is just as informative for those users. Most gallstones never cause symptoms, and sex, age, body weight, rapid weight loss, and pregnancy history all matter more than genotype for whether they do.',
    sources: 'GWAS Catalog (gallstones; cholelithiasis); Buch et al. 2007 Nat Genet; Ferkingstad et al. 2018 Nat Commun',
    markers: [
      {
        rsid: 'rs11887534',
        gene: 'ABCG8',
        alleles: ['G', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'D19H — the C allele encodes histidine and increases biliary cholesterol secretion. Each C copy is associated with roughly 1.8–2.0× the odds of gallstones, one of the largest common-variant effects in this catalog. C frequency is about 7% in European-ancestry cohorts and much lower elsewhere.',
      },
      {
        rsid: 'rs4245791',
        gene: 'ABCG8',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'A second, largely independent ABCG8 signal affecting expression rather than protein sequence. Each T copy is associated with roughly 1.28× the odds of gallstones. T is the common allele (~69%).',
      },
    ],
  },
  {
    id: 'chronic-kidney-disease',
    name: 'Chronic kidney disease',
    aliases: ['CKD', 'kidney disease', 'kidney failure', 'eGFR', 'APOL1', 'UMOD', 'renal'],
    category: 'metabolic',
    impact: 'clinical',
    summary:
      'Two very different kinds of signal sit here. APOL1 is an ancestry-specific, large-effect risk factor found essentially only on African-derived chromosomes, where carrying two risk copies raises the odds of hypertension-attributed kidney failure roughly sevenfold. UMOD and SHROOM3 are ordinary common variants with small effects on filtration rate across populations.',
    caveat:
      'Read the APOL1 caveat carefully. Risk is recessive — it requires two risk copies, from the G1 and/or G2 haplotypes — and G2 is a six-base-pair deletion that this array-based catalog deliberately does not include. Genotyping the G1 SNP alone therefore cannot establish how many risk copies someone carries, and a negative G1 result does not rule out two-copy status. Even with two copies, most carriers never develop kidney failure; a second insult (HIV, interferon, lupus, sickle-cell trait) usually seems to be required. Kidney function is measured directly by creatinine/cystatin C and urine albumin. This is a nephrologist conversation, not an app conclusion.',
    sources: 'GWAS Catalog (EFO_0003884, chronic kidney disease; glomerular filtration rate); Genovese et al. 2010 Science; Köttgen et al. 2010 Nat Genet; Wuttke et al. 2019 (CKDGen)',
    markers: [
      {
        rsid: 'rs73885319',
        gene: 'APOL1',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'S342G, the first of the two SNPs defining the APOL1 G1 haplotype. Reported odds for two APOL1 risk copies are roughly 7× for hypertension-attributed end-stage kidney disease and 10–17× for biopsy-proven FSGS. The allele is common on West African-derived chromosomes (~20%) and essentially absent in European and East Asian ancestry — an entry that is informative for African-ancestry users and silent for everyone else.',
      },
      {
        rsid: 'rs12917707',
        gene: 'UMOD',
        alleles: ['G', 'T'],
        effectAllele: 'T',
        direction: 'protective',
        effect:
          'Upstream of uromodulin, the most abundant protein in urine. Each T copy is associated with higher estimated GFR and roughly 0.8× the odds of chronic kidney disease. T frequency is about 18–20% in European-ancestry cohorts.',
      },
      {
        rsid: 'rs17319721',
        gene: 'SHROOM3',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'One of the first replicated eGFR loci. Each A copy is associated with slightly lower estimated GFR and roughly 1.07× the odds of chronic kidney disease — a small effect. A frequency is about 43%.',
      },
    ],
  },
  {
    id: 'metabolic-syndrome',
    name: 'Metabolic syndrome',
    aliases: ['metabolic syndrome', 'syndrome X', 'insulin resistance', 'waist', 'APOA5', 'GCKR'],
    category: 'metabolic',
    impact: 'modest',
    summary:
      'Metabolic syndrome is a checklist — waist size, triglycerides, HDL, blood pressure, fasting glucose — rather than a disease with its own biology. Its genome-wide scans mostly rediscover the lipid loci, and the two below are the ones that survive across studies.',
    caveat:
      'These are the same variants that appear in the triglycerides entry, because that is what the genetics of metabolic syndrome largely is: the lipid component, seen through a different diagnostic lens. Treat this as one signal counted twice, not extra information. The diagnosis itself is made from a tape measure, a blood pressure cuff, and a fasting blood draw — all of which outrank genotype here.',
    sources: 'GWAS Catalog (EFO_0000195, metabolic syndrome; triglyceride measurement); Kraja et al. 2011 Diabetes; Kristiansson et al. 2012 Circ Cardiovasc Genet',
    markers: [
      {
        rsid: 'rs964184',
        gene: 'APOA5 / ZPR1 (11q23)',
        alleles: ['G', 'C'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'The most consistently reported metabolic syndrome locus, acting through triglycerides and HDL. Each G copy is associated with a higher metabolic-syndrome factor score and roughly +17 mg/dL triglycerides. G frequency is about 13% in European-ancestry cohorts.',
      },
      {
        rsid: 'rs1260326',
        gene: 'GCKR',
        alleles: ['T', 'C'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'P446L. Each T copy is associated with modestly higher odds of meeting metabolic-syndrome criteria (the C allele carries about 0.92× the odds), driven mainly by triglycerides and inflammation markers. T frequency is about 39%.',
      },
    ],
  },
  {
    id: 'vitamin-d',
    name: 'Vitamin D level (25-hydroxyvitamin D)',
    aliases: ['vitamin D', '25(OH)D', 'vitamin D deficiency', 'GC', 'DHCR7', 'CYP2R1'],
    category: 'metabolic',
    impact: 'modest',
    summary:
      'Blood 25(OH)D is mostly set by sunlight, skin pigmentation, latitude, body weight and supplements. Common variants at three loci — the transport protein, the liver hydroxylase and a precursor step — shift the average by small amounts and together explain only a few percent of the variation between people.',
    caveat:
      'These are the standard instruments used in Mendelian-randomisation studies of vitamin D, not a substitute for a 25(OH)D blood test, which is cheap and definitive. Nearly all the effect estimates come from European-ancestry cohorts; GC allele frequencies differ substantially by ancestry, and a widely cited 2013 report suggested people with darker skin have lower vitamin D binding protein and therefore normal bioavailable vitamin D despite a low total 25(OH)D — that finding did not hold up, because the binding-protein assay it used is itself biased by GC genotype, and mass-spectrometry measurements find no ancestry difference. Total 25(OH)D remains the marker to read, whatever your ancestry. Older candidate-gene VDR results (FokI, BsmI) did not replicate at genome-wide scale and are deliberately not listed.',
    sources: 'GWAS Catalog (EFO_0004631, 25-hydroxyvitamin D measurement; harmonized summary statistics); Wang et al. 2010 (SUNLIGHT); Jiang et al. 2018; Manousaki et al. 2020',
    markers: [
      {
        rsid: 'rs2282679',
        gene: 'GC',
        alleles: ['T', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'The largest common-variant effect on vitamin D level: each G copy is associated with roughly 0.19 standard deviations lower 25(OH)D in UK Biobank (on the order of 3–4 nmol/L). GC encodes the vitamin D binding protein, so part of this is transport rather than how much vitamin D reaches tissues.',
      },
      {
        rsid: 'rs12785878',
        gene: 'DHCR7 / NADSYN1',
        alleles: ['G', 'T'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'Near the enzyme that diverts 7-dehydrocholesterol away from the vitamin D precursor. Each G copy is associated with about 0.11 SD lower 25(OH)D. Frequencies differ sharply: G is ~30% in Europeans but ~83% in African-ancestry reference panels.',
      },
      {
        rsid: 'rs10741657',
        gene: 'CYP2R1',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'CYP2R1 is the main liver 25-hydroxylase. Each G copy is associated with roughly 0.08 SD lower 25(OH)D across large UK Biobank analyses. Papers name either allele depending on framing; on the dbSNP forward strand it is G that tracks with lower levels.',
      },
    ],
  },
  {
    id: 'b12',
    name: 'Vitamin B12 level',
    aliases: ['B12', 'cobalamin', 'B12 deficiency', 'FUT2', 'CUBN', 'cubilin'],
    category: 'metabolic',
    impact: 'modest',
    summary:
      'Most low B12 is dietary (vegan or vegetarian diets), age-related malabsorption, autoimmune pernicious anaemia, or a side effect of metformin. Common variants shift measured serum B12 by small amounts, mostly through absorption and transport rather than intake.',
    caveat:
      'Serum B12 measures what is circulating, not what tissues can use — methylmalonic acid or homocysteine is the better check when a level is borderline. Neither variant causes pernicious anaemia, which is autoimmune. The FUT2 marker sits in near-complete linkage with the secretor variant listed separately in this catalog, so the two entries will usually agree.',
    sources: 'GWAS Catalog (EFO_0004620, vitamin B12 measurement); Hazra et al. 2008; Grarup et al. 2013; UK Biobank phecode analyses',
    markers: [
      {
        rsid: 'rs602662',
        gene: 'FUT2',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'protective',
        effect:
          'A missense change at codon 258, in strong linkage with the FUT2 non-secretor stop variant. Each A copy is associated with higher measured serum B12 — reported around tens of pg/mL per copy, a shift within the normal range — probably via gut mucosal antigens and Helicobacter pylori colonisation. A is common in European and African-ancestry groups and almost absent in East Asians (~0.4%), where non-secretor status arises from a different variant, so this marker is uninformative there.',
      },
      {
        rsid: 'rs1801222',
        gene: 'CUBN',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'A missense change at codon 253 of cubilin, the intestinal receptor that takes up the intrinsic-factor–B12 complex. The A allele (~29% in Europeans, ~15–20% elsewhere) is associated with modestly lower serum B12 and with somewhat higher odds of a recorded B12-deficiency diagnosis in UK Biobank.',
      },
    ],
  },
  {
    id: 'bone-density',
    name: 'Bone mineral density / osteoporosis',
    aliases: ['osteoporosis', 'BMD', 'bone density', 'fracture', 'osteopenia', 'LRP5', 'WNT16', 'ESR1'],
    category: 'metabolic',
    impact: 'modest',
    summary:
      'Bone density is highly heritable but extremely polygenic — the largest scans name over a thousand loci. The three below are among the best replicated, and each moves density by a fraction of a standard deviation. Fracture risk follows density, but also age, falls, steroid use and body weight.',
    caveat:
      'A DXA scan measures the thing itself and is what fracture-risk tools actually use; genotype adds very little on top. The effect sizes here come from European-ancestry cohorts, largely heel-ultrasound BMD in UK Biobank, which is a good but imperfect stand-in for hip and spine. None of this speaks to rare high-impact bone disease such as osteogenesis imperfecta or LRP5 loss-of-function syndromes.',
    sources: 'GWAS Catalog (EFO_0003923 bone mineral density, EFO_0009270 heel BMD; harmonized summary statistics); Estrada et al. 2012 (GEFOS-2); Morris et al. 2019 (UK Biobank)',
    markers: [
      {
        rsid: 'rs3736228',
        gene: 'LRP5',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'Ala1330Val in the Wnt co-receptor LRP5. Each T copy (~13% in Europeans, ~2% in African-ancestry groups, ~20% in East Asians) is associated with roughly 0.04–0.11 SD lower bone density and about 1.06× the odds of fracture.',
      },
      {
        rsid: 'rs3801387',
        gene: 'WNT16',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'protective',
        effect:
          'WNT16 governs cortical bone thickness. Each G copy (~26% in Europeans) is associated with roughly 0.13–0.16 SD higher bone density — one of the larger common-variant effects on bone.',
      },
      {
        rsid: 'rs2941740',
        gene: 'ESR1',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'protective',
        effect:
          'In the oestrogen-receptor-alpha region, a locus long suspected in post-menopausal bone loss and later confirmed genome-wide. Each G copy (~42% in Europeans, ~10% in East Asians) is associated with roughly 0.07–0.08 SD higher bone density.',
      },
    ],
  },
  {
    id: 'pcos',
    name: 'Polycystic ovary syndrome',
    aliases: ['PCOS', 'polycystic ovaries', 'hyperandrogenism', 'irregular periods', 'THADA', 'DENND1A'],
    category: 'metabolic',
    impact: 'modest',
    summary:
      'PCOS is a clinical diagnosis — irregular ovulation, raised androgens, polycystic ovaries — and is strongly heritable, but the common variants found so far each nudge the odds only slightly and together account for well under a tenth of that heritability.',
    caveat:
      'Two of these loci were discovered in Han Chinese cohorts. They do replicate in European cohorts, but the lead variant and the effect size differ, so the odds ratios below should not be carried across ancestries. Diagnosis rests on cycle history, androgen levels and ultrasound: PCOS is diagnosed in people carrying none of these alleles and absent in people carrying all of them.',
    sources: 'GWAS Catalog (polycystic ovary syndrome, MONDO_0008487); Chen et al. 2011; Shi et al. 2012; Hayes et al. 2015; Day et al. 2015',
    markers: [
      {
        rsid: 'rs13429458',
        gene: 'THADA',
        alleles: ['A', 'C'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'The 2p21/THADA locus. The A allele carried roughly 1.4–1.5× the odds of PCOS in the Han Chinese discovery and replication cohorts. A is the common allele almost everywhere (~80–90%), so it says little about any one person even though the locus is real.',
      },
      {
        rsid: 'rs2479106',
        gene: 'DENND1A',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'DENND1A is expressed in ovarian theca cells and its splice variant DENND1A.V2 is over-expressed in PCOS theca. Each G copy carried roughly 1.3× the odds of PCOS in Han Chinese cohorts (G ~30% in Europeans, ~18% in East Asians); the locus replicates in Europeans at a nearby variant.',
      },
      {
        rsid: 'rs11031006',
        gene: 'FSHB',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Near FSHB, the follicle-stimulating hormone beta subunit — the clearest European-ancestry PCOS signal. The A allele (~14% in Europeans, ~3% in East Asians) is associated with roughly 1.2–1.4× the odds of PCOS and with higher LH and lower FSH; it also shifts age at menarche and menopause.',
      },
    ],
  },
  {
    id: 'tsh',
    name: 'Thyroid function (TSH level)',
    aliases: ['TSH', 'thyrotropin', 'thyroid', 'hypothyroidism', 'underactive thyroid', 'PDE8B'],
    category: 'metabolic',
    impact: 'modest',
    summary:
      'Where a person sits inside the normal TSH range is substantially heritable — each of us has a fairly tight personal set-point. These variants nudge that set-point. They say little about overt thyroid disease, which is usually autoimmune.',
    caveat:
      'A set-point shift of a tenth of a unit is invisible beside the reference range (roughly 0.4–4 mIU/L) and beside the effect of autoimmune thyroiditis, iodine status, pregnancy or lithium. Neither variant predicts thyroid autoantibodies, and neither should be used to interpret a borderline TSH — the answer there is a repeat blood test with free T4 and antibodies.',
    sources: 'GWAS Catalog (thyroid stimulating hormone measurement); Arnaud-Lopez et al. 2008; Eriksson et al. 2012; Figuerêdo et al. 2024',
    markers: [
      {
        rsid: 'rs4704397',
        gene: 'PDE8B',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'PDE8B degrades cAMP in thyroid cells, the second messenger TSH signals through. Each A copy is associated with higher average TSH — about 0.25 mIU/L per copy in the Sardinian discovery cohort, smaller in later larger samples — and with modestly higher odds of a hypothyroidism diagnosis (~1.17× in a 23andMe cohort, a suggestive rather than genome-wide result).',
      },
      {
        rsid: 'rs10917469',
        gene: 'CAPZB',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        // Lower-normal TSH is not "better" — labelling it protective would
        // imply a health benefit the evidence does not support.
        direction: 'trait',
        effect:
          "Each G copy (~14% in Europeans) is associated with roughly 0.1 units lower TSH in a study of 164,818 European-ancestry individuals. Read this as 'sits lower in the normal range', not as better thyroid health — low-normal TSH is not a health goal, and the same allele has been studied in connection with thyroid nodules and cancer.",
      },
    ],
  },
  {
    id: 'mthfr',
    name: 'MTHFR variants, homocysteine and folate',
    aliases: ['MTHFR', 'C677T', 'A1298C', 'homocysteine', 'folate', 'methylfolate'],
    category: 'metabolic',
    impact: 'modest',
    summary:
      "MTHFR converts folate into the form used to recycle homocysteine back into methionine. The C677T variant reduces the enzyme's activity and raises homocysteine slightly, most of all when folate intake is low. It is common, it is old news, and it is the most over-read result in consumer genetics.",
    caveat:
      "Said plainly: MTHFR status is not a diagnosis and does not call for special supplements. About 1 in 8 Europeans, and up to roughly 1 in 5 people of Latin-American ancestry, carry two copies of 677T, and with adequate folate their homocysteine is close to everyone else's. The American College of Medical Genetics advises against MTHFR testing in thrombophilia and recurrent-pregnancy-loss workups (Hickey et al. 2013), and large trials of homocysteine-lowering B vitamins did not reduce heart attacks, with at most a small effect on stroke. Claims that a 'MTHFR mutation' explains fatigue, anxiety, miscarriage or impaired 'detox', or that it requires methylfolate, are not supported. The place folate genuinely matters is pregnancy — folic acid is recommended for everyone, regardless of genotype.",
    sources: 'GWAS Catalog (EFO_0004578, homocysteine measurement; folate measurement); Frosst et al. 1995; van Meurs et al. 2013; ACMG Practice Guideline, Hickey et al. 2013',
    markers: [
      {
        rsid: 'rs1801133',
        gene: 'MTHFR',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'C677T (Ala222Val). Each A copy is associated with roughly 0.45 µmol/L higher plasma homocysteine and modestly lower serum folate; A/A homozygotes show around 30% of normal enzyme activity in the lab. Frequency varies widely — about 37% in Europeans, ~47% in Latin-American samples, ~9% in African-ancestry samples.',
      },
      {
        rsid: 'rs1801131',
        gene: 'MTHFR',
        alleles: ['T', 'G'],
        effectAllele: 'G',
        // Not a risk allele: no measurable effect on its own, so colouring it
        // as one would be exactly the MTHFR over-interpretation this entry warns about.
        direction: 'trait',
        effect:
          'A1298C (Glu429Ala). It lowers enzyme activity in vitro, but on its own it shows no measurable effect on homocysteine or folate in large population studies — it has never reached genome-wide significance for either. The interest is in people carrying one 677T and one 1298C, whose enzyme activity resembles a mild 677 homozygote.',
      },
    ],
  },
  {
    id: 'restless-legs',
    name: 'Restless legs syndrome',
    aliases: ['RLS', 'Willis-Ekbom disease', 'periodic limb movements', 'leg jitters', 'MEIS1', 'BTBD9'],
    category: 'neurological',
    impact: 'notable',
    summary:
      'RLS is one of the few common conditions where common variants carry sizeable effects — the MEIS1 signal is among the strongest common-variant associations known outside immunology. Even so, many carriers never have symptoms, and iron status is a large part of the picture.',
    caveat:
      'All the effect sizes here come from European-ancestry case-control studies in which cases met clinical criteria; these variants are far less studied in other populations. RLS is diagnosed from history, and the practical first step is checking ferritin — iron deficiency, pregnancy, kidney failure and some antidepressants all provoke it, and those causes are treatable regardless of genotype.',
    sources: 'GWAS Catalog (restless legs syndrome, MONDO_0005391); Winkelmann et al. 2007; Stefansson et al. 2007; Winkelmann et al. 2011; Tilch et al. 2022',
    markers: [
      {
        rsid: 'rs2300478',
        gene: 'MEIS1',
        alleles: ['T', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'The strongest RLS locus. Each G copy (~25% in Europeans) is associated with roughly 1.6–1.7× the odds of RLS, replicated in every large scan since 2007. Some papers report this allele as C, from the opposite strand.',
      },
      {
        rsid: 'rs3923809',
        gene: 'BTBD9',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Each A copy is associated with roughly 1.5–1.9× the odds of RLS; in the Icelandic discovery cohort it also tracked with periodic limb movements in sleep and with lower serum ferritin. A is the common allele in Europeans (~69%), so it is a weak discriminator between individuals.',
      },
      {
        rsid: 'rs12593813',
        gene: 'MAP2K5 / SKOR1',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'Each G copy (~64% in Europeans, ~25% in African-ancestry groups) is associated with roughly 1.4–1.5× the odds of RLS.',
      },
    ],
  },
  {
    id: 'migraine',
    name: 'Migraine',
    aliases: ['headache', 'migraine with aura', 'migraine without aura', 'LRP1', 'TRPM8', 'sick headache'],
    category: 'neurological',
    impact: 'modest',
    summary:
      'Migraine is highly polygenic — the largest meta-analyses now count over a hundred loci, and no single common variant matters much on its own. The markers below are among the best-replicated; each shifts the odds by roughly 10 to 20 percent in the populations studied.',
    caveat:
      'Migraine is diagnosed from the pattern of attacks, not from DNA. These variants do not separate migraine with aura from migraine without aura in any way that is useful for one person — the 2016 and later meta-analyses found most loci shared across both subtypes. The rare monogenic forms (familial hemiplegic migraine: CACNA1A, ATP1A2, SCN1A) are a different disease and are not covered by consumer arrays. Almost all of this work is in European-ancestry cohorts.',
    sources: 'GWAS Catalog (MONDO_0005277 migraine disorder); Gormley et al. 2016 Nat Genet; Chasman et al. 2011; Anttila et al. 2010',
    markers: [
      {
        rsid: 'rs11172113',
        gene: 'LRP1',
        alleles: ['T', 'C'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'The most consistently replicated common migraine signal. Each T copy is associated with roughly 1.11x the odds of migraine in large European meta-analyses (p < 1e-49), and the effect shows up in both the aura and non-aura subtypes.',
      },
      {
        rsid: 'rs10166942',
        gene: 'TRPM8',
        alleles: ['T', 'C'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'TRPM8 encodes the cold and menthol receptor on sensory nerves. Each T copy is associated with about 1.1 to 1.18x the odds of migraine. Frequency differs sharply by ancestry — T is around 83% in Europeans but under 10% in African-ancestry groups — and the association was measured almost entirely in Europeans.',
      },
      {
        rsid: 'rs2651899',
        gene: 'PRDM16',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          "First found in the Women's Genome Health Study and replicated in later meta-analyses; each C copy is associated with roughly 1.09 to 1.11x the odds of migraine, with no strong subtype preference.",
      },
      {
        rsid: 'rs1835740',
        gene: 'MTDH / PGCP (8q22.1)',
        alleles: ['T', 'C'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          "The first migraine GWAS hit (2010), where it was reported as the 'A' allele on the opposite strand — T is the same allele read on the dbSNP forward strand. Reported at about 1.18x the odds, strongest in clinic-collected migraine with aura, but an independent Spanish replication was null and later much larger meta-analyses have not kept it among the leading signals. Best treated as historical.",
      },
    ],
  },
  {
    id: 'insomnia',
    name: 'Insomnia',
    aliases: ['trouble sleeping', 'sleeplessness', 'sleep problems', 'poor sleep', 'MEIS1'],
    category: 'neurological',
    impact: 'modest',
    summary:
      'Biobank studies of hundreds of thousands of people have found real, replicated genetic signals for insomnia symptoms — but each one is tiny, and common variants together explain only around 7% of the variation in liability. Two of the strongest overlap with restless legs syndrome, which may be part of what is being measured.',
    caveat:
      'Insomnia GWAS rest on self-reported questionnaire answers in biobanks, a noisy phenotype that blends short-term and chronic sleeplessness. The per-allele effects below are in the 2 to 20 percent range on odds, which is nothing an individual can feel or plan around. Nothing here changes what actually helps: sleep timing, light, caffeine and alcohol, and cognitive behavioural therapy for insomnia. Estimates are from European-ancestry samples.',
    sources: 'GWAS Catalog (MONDO_0013600 insomnia; EFO_0007876); Jansen et al. 2019 Nat Genet; Lane et al. 2019 Nat Genet',
    markers: [
      {
        rsid: 'rs113851554',
        gene: 'MEIS1',
        alleles: ['G', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'The same low-frequency MEIS1 allele that dominates restless legs syndrome. In insomnia GWAS it is associated with roughly 1.14 to 1.26x the odds of reporting insomnia — plausibly in part because RLS itself disrupts sleep. About 5% frequency in Europeans, near-absent in East Asian samples.',
      },
      {
        rsid: 'rs3923809',
        gene: 'BTBD9',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Genome-wide significant for insomnia in over 1.3 million people, but the effect is very small — about 1.05x the odds per A copy (Jansen et al. 2019). The same allele is a much stronger restless-legs signal.',
      },
      {
        rsid: 'rs12552',
        gene: 'OLFM4 region (13q14)',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'A small but firmly replicated signal: each A copy is associated with a slightly higher chance of reporting insomnia, and the same allele appears in depression GWAS at roughly 1.04x the odds. One of several loci where sleep and mood signals overlap.',
      },
    ],
  },
  {
    id: 'narcolepsy',
    name: 'Narcolepsy (type 1, with cataplexy)',
    aliases: ['cataplexy', 'sleep attacks', 'hypocretin', 'orexin', 'HLA-DQB1*06:02', 'excessive daytime sleepiness'],
    category: 'neurological',
    impact: 'modest',
    summary:
      'Narcolepsy with cataplexy is close to an HLA disease: almost every case carries HLA-DQB1*06:02, an allele that raises odds by roughly 250-fold. That allele cannot be typed from a consumer array. The two markers below are the nearest an array gets — an imperfect haplotype tag and a secondary signal in the same region.',
    caveat:
      'Neither SNP is a substitute for HLA typing, and neither can rule DQB1*06:02 in or out. The tagging works only in European-ancestry samples: rs3135388 is almost absent in East Asian populations even though DQB1*06:02 is present there. The asymmetry also matters — roughly 12 to 25 percent of Europeans carry DQB1*06:02 and virtually none of them develop narcolepsy, which is rare (around 25 to 50 per 100,000). Diagnosis comes from a sleep study or CSF hypocretin measurement, never from genotype.',
    sources: 'GWAS Catalog (MONDO_0016158 narcolepsy-cataplexy syndrome); Hor et al. 2010 Nat Genet; Tafti et al. 2014 Sleep; Mignot et al. HLA typing literature',
    markers: [
      {
        rsid: 'rs3135388',
        gene: 'HLA-DRA / DRB1*15:01-DQB1*06:02 haplotype tag',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Tags the DRB1*15:01-DQB1*06:02 haplotype, which in Europeans is the haplotype that carries the narcolepsy allele DQB1*06:02 (odds ratio around 250 for narcolepsy with cataplexy). The tagging is partial, so this SNP is a hint about HLA background, not a test. Frequency around 12% in Europeans, under 1% in East Asians.',
      },
      {
        rsid: 'rs2858884',
        gene: 'HLA class II region (6p21)',
        alleles: ['A', 'C'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'In European studies that deliberately matched cases and controls on DQB1*06:02, the A allele was associated with roughly 1.8x the odds of narcolepsy with cataplexy (Hor 2010, replicated by Tafti 2014). This is a secondary HLA class II signal — the C allele appears to travel with protective DQ haplotypes such as DQB1*06:03 — and it is not the DQB1*06:02 status itself.',
      },
    ],
  },
  {
    id: 'schizophrenia',
    name: 'Schizophrenia',
    aliases: ['psychosis', 'CACNA1C', 'ZNF804A', 'MIR137', 'schizoaffective'],
    category: 'neurological',
    impact: 'modest',
    summary:
      'Schizophrenia is one of the most polygenic conditions studied — the current PGC analyses implicate hundreds of loci, each contributing a fraction of a percent. The three below are historically important, well-replicated single markers, but they say essentially nothing about any one person.',
    caveat:
      'This is the clearest case where a polygenic score is useless at the individual level. Even a full genome-wide score built from millions of variants separates cases from controls poorly; three SNPs do far less. Schizophrenia affects under 1% of people, the strongest known risk factors are not on any array (rare copy-number variants, which this catalog excludes by design), and family history and clinical assessment carry all the weight. The ZNF804A result in particular did not replicate in Han Chinese meta-analyses — these estimates are European.',
    sources: "GWAS Catalog (MONDO_0005090); PGC Schizophrenia Working Group 2011, 2014, 2022; O'Donovan et al. 2008",
    markers: [
      {
        rsid: 'rs1344706',
        gene: 'ZNF804A',
        alleles: ['A', 'C'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          "The first genome-wide significant psychosis variant (2008), reported there as 'T' on the opposite strand — A is the same allele on the dbSNP forward strand. Each A copy is associated with roughly 1.1x the odds of schizophrenia in Europeans; meta-analyses in Han Chinese samples did not find the association.",
      },
      {
        rsid: 'rs1006737',
        gene: 'CACNA1C',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'An intronic variant in an L-type calcium channel subunit. Each A copy is associated with about 1.10x the odds of schizophrenia in PGC data, and the same allele also associates with bipolar disorder — one of the clearest examples of shared psychiatric risk.',
      },
      {
        rsid: 'rs1625579',
        gene: 'MIR137',
        alleles: ['G', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'Near the microRNA-137 gene, which regulates several other schizophrenia-associated genes. Each T copy is associated with roughly 1.12x the odds. T is the common allele in Europeans (about 79%), so it is background risk rather than a marker of a small group.',
      },
    ],
  },
  {
    id: 'bipolar',
    name: 'Bipolar disorder',
    aliases: ['manic depression', 'bipolar I', 'mood disorder', 'CACNA1C', 'TRANK1'],
    category: 'neurological',
    impact: 'modest',
    summary:
      'Bipolar disorder is strongly heritable in families but highly polygenic in the genome: the largest studies find dozens of loci, each moving odds by only 10 to 20 percent. The three below are the most durable single markers across successive PGC waves.',
    caveat:
      'These variants cannot predict anything for an individual, and they do not distinguish bipolar disorder from schizophrenia or depression — CACNA1C in particular is associated with all three. Bipolar disorder affects about 1 to 2 percent of people, so even a doubled odds ratio would leave absolute risk small, and nothing here approaches that. Diagnosis is clinical, and family history is far more informative than these markers. Estimates come from European-ancestry cohorts.',
    sources: 'GWAS Catalog (MONDO_0004985); Mullins et al. 2021 Nat Genet (PGC BD3); Stahl et al. 2019; Ferreira et al. 2008; Chen et al. 2013',
    markers: [
      {
        rsid: 'rs1006737',
        gene: 'CACNA1C',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Each A copy is associated with roughly 1.15 to 1.18x the odds of bipolar disorder — one of the first and most reproducible psychiatric GWAS findings. The same allele associates with schizophrenia at a similar size.',
      },
      {
        rsid: 'rs9834970',
        gene: 'TRANK1',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'Each C copy is associated with about 1.12x the odds of bipolar disorder; equivalently, the T allele is reported as protective at roughly 0.90. Replicated across several PGC waves and roughly evenly split in frequency in Europeans.',
      },
      {
        rsid: 'rs12576775',
        gene: 'TENM4 (ODZ4)',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'Each G copy is associated with about 1.14 to 1.18x the odds of bipolar disorder. G is uncommon (around 19% in Europeans, under 10% in East Asian and African reference panels), and the association was tested in European-ancestry samples.',
      },
    ],
  },
  {
    id: 'depression',
    name: 'Major depression',
    aliases: ['depression', 'MDD', 'low mood', 'major depressive disorder', 'NEGR1'],
    category: 'neurological',
    impact: 'modest',
    summary:
      'Depression GWAS only started working once sample sizes reached the hundreds of thousands, and what they found is a very flat landscape: many variants, each moving the odds by three or four percent. The three below are replicated across the largest studies, and they are honestly about as small as a real genetic association gets.',
    caveat:
      "Per-allele odds ratios of about 1.04 are invisible at the individual level — carrying all six risk alleles here would not meaningfully change anyone's outlook. Even complete polygenic scores for depression explain a low single-digit percentage of variance and cannot predict who becomes depressed. Life circumstances, loss, chronic illness and adversity dominate. Nothing in this entry should influence whether someone seeks help. The samples are overwhelmingly European-ancestry, and the phenotype in the biggest studies is often self-reported or minimally screened.",
    sources: 'GWAS Catalog (MONDO_0002009); Wray et al. 2018 Nat Genet (PGC MDD); Howard et al. 2019 Nat Neurosci; Hyde et al. 2016',
    markers: [
      {
        rsid: 'rs1432639',
        gene: 'NEGR1',
        alleles: ['C', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Near NEGR1, a neuronal growth regulator also linked to body weight. Each A copy is associated with roughly 1.04x the odds of major depression — one of the strongest single signals in a field where that counts as strong.',
      },
      {
        rsid: 'rs12552',
        gene: 'OLFM4 region (13q14)',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Each A copy is associated with about 1.04x the odds of major depression across the 23andMe and PGC datasets. The same allele shows a small association with insomnia symptoms.',
      },
      {
        rsid: 'rs454214',
        gene: 'TMEM161B / MEF2C',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'An intergenic variant between TMEM161B and the neurodevelopmental transcription factor MEF2C. Each T copy is associated with roughly 1.03 to 1.04x the odds of major depression in large European meta-analyses.',
      },
    ],
  },
  {
    id: 'intracranial-aneurysm',
    name: 'Intracranial aneurysm',
    aliases: ['brain aneurysm', 'subarachnoid hemorrhage', 'cerebral aneurysm', 'SOX17', 'EDNRA', '9p21'],
    category: 'cardiovascular',
    impact: 'notable',
    summary:
      'Unruptured brain aneurysms are common (a few percent of adults) and rupture is rare. Several loci are replicated across European and Japanese cohorts, with per-allele odds around 1.2 to 1.35 — larger than most complex-disease signals, but still small next to smoking and blood pressure.',
    caveat:
      "Aneurysmal subarachnoid hemorrhage affects roughly 9 people per 100,000 per year, so even a 1.3x odds ratio leaves absolute risk low. What actually drives screening decisions is family history — typically two or more affected first-degree relatives — plus smoking and hypertension, both of which are modifiable and outweigh these variants. No consumer array result should prompt imaging on its own; that is a neurologist's or neurosurgeon's call. The 9p21 signal sits in the same stretch as the coronary artery disease locus already in this catalog, but the aneurysm-associated SNPs are nearby rather than identical.",
    sources: 'GWAS Catalog (MONDO_0005291 brain aneurysm); Yasuno et al. 2010 Nat Genet; Yasuno et al. 2011 PNAS (EDNRA); Bilguvar et al. 2008 Nat Genet; Bakker et al. 2020 Nat Genet',
    markers: [
      {
        rsid: 'rs1333040',
        gene: 'CDKN2B-AS1 (9p21)',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'Each T copy is associated with roughly 1.29 to 1.32x the odds of intracranial aneurysm across two independent European meta-analyses. T is the common allele (about 57% in Europeans), so it contributes broadly to background risk.',
      },
      {
        rsid: 'rs9298506',
        gene: 'SOX17',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'SOX17 governs endothelial development. Each A copy is associated with about 1.28 to 1.35x the odds of intracranial aneurysm in European cohorts, with support from Japanese samples in the same studies.',
      },
      {
        rsid: 'rs6841581',
        gene: 'EDNRA',
        alleles: ['G', 'A'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'The endothelin receptor A locus. Each G copy is associated with about 1.24x the odds of intracranial aneurysm in a European plus East Asian meta-analysis of over 10,000 cases, with earlier European and Japanese work putting it at 1.22x. G is the common allele (about 85% in Europeans, 70-80% in East Asians), so most people carry two copies. The opposite allele, A, is the one carrying a small coronary artery disease and heart attack association (around 1.07-1.08x) — the aneurysm and coronary risk alleles at EDNRA point in opposite directions rather than marking one shared arterial-wall risk.',
      },
    ],
  },
  {
    id: 'glaucoma',
    name: 'Glaucoma (primary open-angle)',
    aliases: ['POAG', 'eye pressure', 'intraocular pressure', 'optic nerve', 'TMCO1', 'vision loss'],
    category: 'neurological',
    impact: 'notable',
    summary:
      'Primary open-angle glaucoma has some of the larger common-variant effects in complex disease, acting partly through eye pressure and partly through optic nerve head structure. It is also one of the few conditions here where the practical response is simple and effective: get your eyes checked.',
    caveat:
      'Several of these odds ratios come from case series deliberately enriched for severe visual field loss, so they are upper bounds — in unselected populations the effects are smaller. Glaucoma is diagnosed by an eye exam (pressure, optic disc, visual field), never by genotype, and early treatment preserves sight, which is why family history and regular checks after age 40 matter far more than any SNP. Angle-closure glaucoma and exfoliation glaucoma are different diseases with different genetics; at the exfoliation locus LOXL1 the risk allele actually reverses between European and Japanese populations, which is why no LOXL1 marker is listed here. The estimates below are mainly European, with Japanese replication for SIX1/SIX6.',
    sources: 'GWAS Catalog (MONDO_0005338 open-angle glaucoma); Burdon et al. 2011 Nat Genet; Gharahkhani et al. 2014; Wiggs et al. 2012; Osman et al. 2012; Gao et al. 2018',
    markers: [
      {
        rsid: 'rs4656461',
        gene: 'TMCO1',
        alleles: ['G', 'A'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'Each G copy is associated with roughly 1.38 to 1.51x the odds of open-angle glaucoma in European case-control studies, and with about 0.29 mmHg higher intraocular pressure in a 115,000-person analysis. G is uncommon (around 14% in Europeans, about 1% in East Asians).',
      },
      {
        rsid: 'rs4977756',
        gene: 'CDKN2B-AS1 (9p21)',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Each A copy is associated with roughly 1.39 to 1.48x the odds in cohorts selected for advanced or normal-tension glaucoma; the effect is smaller in unselected cases. A is the more common allele in Europeans (about 60%).',
      },
      {
        rsid: 'rs10483727',
        gene: 'SIX1 / SIX6',
        alleles: ['T', 'C'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          "Each T copy is associated with about 1.27 to 1.32x the odds of open-angle glaucoma, replicated in both European and Japanese cohorts, and with a larger optic cup — the structural change clinicians look for. Some papers report this as the 'A' allele, which is the same allele on the opposite strand.",
      },
    ],
  },
  {
    id: 'colorectal-cancer',
    name: 'Colorectal cancer (common variants)',
    aliases: ['bowel cancer', 'colon cancer', 'rectal cancer', 'Lynch syndrome', '8q24', 'SMAD7'],
    category: 'cancer',
    impact: 'modest',
    summary:
      'Common variants each nudge colorectal risk a little. The two below were among the first GWAS hits found and are among the most reproducible, but together they explain only a sliver of inherited risk — far less than a family history tells you.',
    caveat:
      'These are small shifts in risk, not a screening test. Consumer arrays do NOT cover Lynch syndrome (MLH1, MSH2, MSH6, PMS2, EPCAM), familial adenomatous polyposis (APC), or TP53 — the high-penetrance genes behind most strong family histories. A quiet result here says nothing about any of them. Decisions about when to start colonoscopy belong with a clinician and your family history.',
    sources: 'GWAS Catalog (EFO_0005842); Tomlinson et al. 2007; Zanke et al. 2007; Broderick et al. 2007',
    markers: [
      {
        rsid: 'rs6983267',
        gene: 'CASC8 / CCAT2 (8q24.21)',
        alleles: ['G', 'T'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'An enhancer variant in the 8q24 gene desert that physically loops to MYC. Each G copy is associated with roughly 1.2x the odds of colorectal cancer in European-ancestry studies. G is at about 50% in Europeans but about 95% in African-ancestry groups, where the association is much less clear.',
      },
      {
        rsid: 'rs4939827',
        gene: 'SMAD7',
        alleles: ['T', 'C'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'An intronic variant in SMAD7, a brake on TGF-beta signalling. Each T copy is associated with roughly 1.15x the odds; C/C homozygotes had about 0.73x the odds in the discovery study. T is at about 53% in Europeans and about 30% in East Asian and African-ancestry groups.',
      },
    ],
  },
  {
    id: 'lung-cancer',
    name: 'Lung cancer (common variants)',
    aliases: ['lung', 'CHRNA3', 'TERT', 'smoking', 'adenocarcinoma', 'oncology'],
    category: 'cancer',
    impact: 'modest',
    summary:
      'The two best-replicated common signals for lung cancer sit in a nicotinic receptor cluster on 15q25 and at the telomerase gene TERT. The 15q25 signal works mostly through how heavily a smoker smokes; the TERT signal is largest for adenocarcinoma and shows up in never-smokers too.',
    caveat:
      "Smoking dwarfs all of this. These are small shifts on top of a risk that tobacco moves by tenfold or more, and in someone who has never smoked the absolute effect is very small. Note also that rs1051730 sits in the same 15q25 block as rs16969968 in the 'Nicotine dependence' entry — they are near-inseparable markers of one signal, not two independent findings. Arrays do not detect the somatic EGFR or ALK changes that guide lung-cancer treatment.",
    sources: 'GWAS Catalog (EFO_0001071); Thorgeirsson et al. 2008; Hung et al. 2008; McKay et al. 2008; Wang et al. 2008',
    markers: [
      {
        rsid: 'rs1051730',
        gene: 'CHRNA3',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'A synonymous change in CHRNA3, in near-complete linkage with the CHRNA5 D398N variant. Each A copy is associated with about 1.3x the odds of lung cancer in European-ancestry studies, largely through heavier smoking. A is at ~37% in Europeans but under 3% in East Asians, so this signal barely exists there.',
      },
      {
        rsid: 'rs2736100',
        gene: 'TERT',
        alleles: ['C', 'A'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'An intronic variant in telomerase reverse transcriptase; the C allele tracks with longer telomeres. Each C copy is associated with roughly 1.3-1.4x the odds of lung adenocarcinoma. Unlike the 15q25 signal it also appears in never-smokers, and it replicates in East Asian cohorts. C is near 50% in Europeans, ~42% in East Asians.',
      },
    ],
  },
  {
    id: 'melanoma',
    name: 'Melanoma (common variants)',
    aliases: ['skin cancer', 'MC1R', 'red hair', 'SLC45A2', 'sun', 'oncology'],
    category: 'cancer',
    impact: 'notable',
    summary:
      'Most common-variant melanoma risk runs through pigmentation. The two below are among the strongest known: a red-hair and freckling allele of MC1R, and a pigmentation switch at SLC45A2 that is near-fixed in Europeans and rare elsewhere.',
    caveat:
      'Real but still relative shifts, and they largely restate what fair skin, freckling and burn history already say. Arrays do NOT cover the high-penetrance melanoma genes (CDKN2A, CDK4, BAP1), and while some consumer chips genotype a short list of specific BRCA1/BRCA2 variants they do not sequence those genes, so most pathogenic variants are invisible — a clear result here says nothing about a strong family history. Ancestry dominates: both alleles are essentially European-specific, so absence in someone of non-European descent mostly means the variant was never there to find. Shade, sunscreen and getting changing moles looked at are worth the same either way.',
    sources: 'GWAS Catalog (EFO_0000389); Bishop et al. 2009; Law et al. 2015; UK Biobank / deCODE skin-cancer meta-analyses',
    markers: [
      {
        rsid: 'rs1805007',
        gene: 'MC1R',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          "R151C, one of the classic MC1R red-hair 'R' alleles. Each T copy is associated with roughly 1.6x the odds of cutaneous melanoma and about 4x the odds of freckling; two copies is a common route to red hair. T is at ~7% in Europeans and essentially absent in African, East Asian and South Asian groups.",
      },
      {
        rsid: 'rs16891982',
        gene: 'SLC45A2',
        alleles: ['C', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'L374F, a major light-pigmentation allele. European-ancestry studies report roughly 2x the odds of cutaneous melanoma per G copy. G is at ~94% in Europeans and under 5% in African and East Asian groups — so it separates populations far better than it separates individuals within one, and most people of European descent carry two copies.',
      },
    ],
  },
  {
    id: 'basal-cell-carcinoma',
    name: 'Basal cell carcinoma & keratinocyte skin cancer',
    aliases: ['BCC', 'skin cancer', 'IRF4', 'squamous cell carcinoma', 'actinic keratosis', 'sun damage'],
    category: 'cancer',
    impact: 'modest',
    summary:
      'Basal and squamous cell carcinomas are the most common cancers in humans, and their common-variant signals are unusually clean: pigmentation variants at MC1R and IRF4 turn up in essentially every large skin-cancer study, with effect sizes bigger than typical GWAS hits.',
    caveat:
      "Small-to-moderate shifts on a risk that cumulative sun exposure mostly sets. Basal cell carcinoma is common and highly treatable, and nothing here changes the advice on shade, sunscreen and getting new or changing lesions checked. Both alleles are near-absent outside European ancestry, so a 'no risk allele' result in someone of non-European descent is uninformative rather than reassuring. Arrays do not cover PTCH1 mutations (basal cell nevus / Gorlin syndrome).",
    sources: 'GWAS Catalog (EFO_0004193); Stacey et al. 2009; Han et al. 2011; Chahal et al. 2016; Liyanage et al. 2019',
    markers: [
      {
        rsid: 'rs1805007',
        gene: 'MC1R',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'R151C. Each T copy is associated with roughly 1.34x the odds of basal cell carcinoma and a similar or larger shift for cutaneous squamous cell carcinoma and actinic keratosis. Carried by ~7% of Europeans, near-zero elsewhere.',
      },
      {
        rsid: 'rs12203592',
        gene: 'IRF4',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'An intronic regulatory variant driving freckling, hair colour and sun sensitivity. Each T copy is associated with roughly 1.41x the odds of basal cell carcinoma and about 1.6x for squamous cell carcinoma. T is at ~12% in Europeans and essentially absent in East Asian and African-ancestry groups.',
      },
    ],
  },
  {
    id: 'thyroid-cancer',
    name: 'Thyroid cancer (common variants)',
    aliases: ['thyroid', 'papillary thyroid carcinoma', 'FOXE1', 'NKX2-1', 'goitre'],
    category: 'cancer',
    impact: 'modest',
    summary:
      'Papillary thyroid cancer has two unusually strong common-variant signals for a solid tumour, near FOXE1 on 9q22 and NKX2-1 on 14q13 — both thyroid transcription factors. They still explain only a modest part of inherited risk.',
    caveat:
      'Relative shifts on a small baseline. Thyroid cancer is uncommon, and papillary tumours are usually very treatable; a large share of what gets detected is overdiagnosis from incidental imaging, so more scanning is not automatically better. Arrays do not cover the RET mutations behind medullary thyroid cancer and MEN2. The FOXE1 allele is roughly three times rarer outside European and American populations.',
    sources: 'GWAS Catalog (EFO_0002892); Gudmundsson et al. 2009; Takahashi et al. 2010; Gudmundsson et al. 2012',
    markers: [
      {
        rsid: 'rs965513',
        gene: 'FOXE1 / PTCSC2 (9q22.33)',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'The strongest common thyroid-cancer variant found so far. Each A copy is associated with roughly 1.75x the odds of papillary thyroid carcinoma in European-ancestry studies, and the same allele tracks with lower TSH and hypothyroidism. A is at ~34% in Europeans but only ~11-13% in African- and East-Asian-ancestry groups.',
      },
      {
        rsid: 'rs944289',
        gene: '14q13.3, near NKX2-1',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'An intergenic variant at 14q13.3, in the promoter region of the thyroid lncRNA PTCSC3, about 336 kb from the thyroid transcription factor NKX2-1. Each T copy is associated with roughly 1.37x the odds of thyroid carcinoma. T is at ~58% in Europeans, ~46% in East Asians and ~15% in African-ancestry groups.',
      },
    ],
  },
  {
    id: 'bladder-cancer',
    name: 'Bladder cancer (common variants)',
    aliases: ['bladder', 'urothelial carcinoma', 'PSCA', '8q24', 'smoking'],
    category: 'cancer',
    impact: 'modest',
    summary:
      'Two well-replicated signals: one in the 8q24 gene desert (an independent block from the prostate and colorectal hits there) and one at PSCA. Both are small next to the things that actually drive bladder cancer — smoking and occupational chemical exposure.',
    caveat:
      'Small shifts. Smoking accounts for roughly half of bladder cancers and matters far more than either variant. Two of the better-known genetic risk factors are missing here on purpose: the NAT2 slow-acetylator haplotype needs several SNPs read together, and the GSTM1 risk factor is a whole-gene deletion, not a single base — arrays generally cannot report either reliably.',
    sources: 'GWAS Catalog (EFO_0000292); Kiemeney et al. 2008; Wu et al. 2009; Rothman et al. 2010',
    markers: [
      {
        rsid: 'rs9642880',
        gene: 'CASC11 (8q24.21, near MYC)',
        alleles: ['G', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'Each T copy is associated with roughly 1.2x the odds of urothelial bladder cancer; T/T homozygotes had about 1.49x in the discovery study. T is at ~45% in Europeans. This is a separate 8q24 block from the prostate and colorectal variants, and it was not associated with those cancers.',
      },
      {
        rsid: 'rs2294008',
        gene: 'PSCA',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'A first-exon variant that lowers PSCA transcription. Each T copy is associated with roughly 1.13x the odds of bladder cancer, and the same allele is associated with gastric cancer (~1.3x) and with lower risk of duodenal ulcer. T is at ~45% in Europeans and ~34% in East Asians.',
      },
    ],
  },
  {
    id: 'testicular-cancer',
    name: 'Testicular germ cell tumour (common variants)',
    aliases: ['testicular cancer', 'TGCT', 'seminoma', 'KITLG', 'DMRT1'],
    category: 'cancer',
    impact: 'notable',
    summary:
      'Testicular germ cell tumour has the largest common-variant effects of any cancer studied so far. The KITLG variant below more than doubles the odds per copy — very unusual for a GWAS hit — and its frequency differences across populations run in the same direction as the long-known incidence gap between them.',
    caveat:
      'A large relative effect on a small baseline: lifetime risk is well under 1%, and testicular cancer is among the most curable cancers even when caught late. Nothing here justifies imaging or tumour-marker testing in someone without symptoms. Note too that the risk allele is the common one — about 96% of men of European descent carry at least one copy, and roughly two-thirds carry two — so it is a poor way to single anyone out. Established in European-ancestry cohorts; much less studied elsewhere.',
    sources: 'GWAS Catalog (EFO_0005088); Rapley et al. 2009; Kanetsky et al. 2009; Turnbull et al. 2010; Ruark et al. 2013',
    markers: [
      {
        rsid: 'rs995030',
        gene: 'KITLG',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          "In the 3' region of KITLG (stem cell factor), which germ cells depend on. Each G copy is associated with roughly 2.3-2.6x the odds of testicular germ cell tumour. G is at ~80% in Europeans and ~25% in African-ancestry groups — the same direction as the roughly four-to-fivefold incidence difference between those groups.",
      },
      {
        rsid: 'rs755383',
        gene: 'DMRT1',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'Near DMRT1, a sex-determination and germ-cell gene on 9p24. Each T copy is associated with roughly 1.4-1.5x the odds. T is at ~62% in Europeans and ~65% in East Asians.',
      },
    ],
  },
  {
    id: 'pancreatic-cancer',
    name: 'Pancreatic cancer (common variants)',
    aliases: ['pancreas', 'ABO', 'blood group', 'PanScan', 'oncology'],
    category: 'cancer',
    impact: 'modest',
    summary:
      'The clearest common-variant signal for pancreatic cancer sits in the ABO blood-group gene — non-O groups carry slightly higher risk, a link noticed in hospital records decades before GWAS confirmed it. A second replicated signal sits at 13q22.1.',
    caveat:
      'Small shifts on a disease with no effective population screening, so there is nothing here to act on. rs505922 only tags blood group indirectly: the actual O-determining change is a single-base deletion (rs8176719) this catalog does not carry, and rs505922 stands in for it only through linkage disequilibrium, which makes it an imperfect proxy. Arrays also do not cover the rare high-penetrance causes of familial pancreatic cancer (BRCA2, PALB2, CDKN2A, STK11, the Lynch genes, hereditary pancreatitis). Smoking, chronic pancreatitis and long-standing diabetes matter far more.',
    sources: 'GWAS Catalog (EFO_0002618); Amundadottir et al. 2009; Petersen et al. 2010; Klein et al. 2018',
    markers: [
      {
        rsid: 'rs505922',
        gene: 'ABO',
        alleles: ['C', 'T'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'An intronic ABO variant that tags the non-O blood groups; the T allele tracks the O allele. Each C copy is associated with roughly 1.2x the odds of pancreatic cancer in the original PanScan study and about 1.27x in a large 2018 meta-analysis. C is at ~37% in Europeans, ~38% in East Asians and ~22% in admixed American groups.',
      },
      {
        rsid: 'rs9543325',
        gene: '13q22.1 (KLF5/KLF12 region)',
        alleles: ['C', 'T'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'An intergenic variant found by the PanScan consortium. Each C copy is associated with roughly 1.24x the odds. C is at ~39% in Europeans and ~43% in East Asians, and ~95% in African-ancestry groups, where the association is untested.',
      },
    ],
  },
  {
    id: 'ovarian-cancer',
    name: 'Ovarian cancer (common variants)',
    aliases: ['ovary', 'BNC2', '9p22', 'serous', 'BRCA', 'oncology'],
    category: 'cancer',
    impact: 'modest',
    summary:
      'Two well-replicated common variants, both reported in the protective direction: their less-common alleles are associated with lower risk, most clearly for serous tumours. Together they account for a very small share of inherited ovarian-cancer risk.',
    caveat:
      'Small shifts, and this does NOT screen for BRCA1/BRCA2 or the Lynch syndrome mismatch-repair genes — the mutations behind most strongly inherited ovarian cancer are rare, scattered across large genes, and not on consumer arrays. A quiet result here says nothing about them. With a family history of ovarian or breast cancer, ask about clinical genetic testing rather than reading anything into this. Effect estimates come from European-ancestry consortia.',
    sources: 'GWAS Catalog (EFO_1001513); Song et al. 2009; Goode et al. 2010; Bolton et al. 2010; Pharoah et al. 2013',
    markers: [
      {
        rsid: 'rs3814113',
        gene: '9p22.2, ~44 kb upstream of BNC2',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'protective',
        effect:
          'The first ovarian-cancer GWAS hit. Each C copy is associated with roughly 0.8x the odds of epithelial ovarian cancer (about 0.77x for serous tumours) — so it is the common T allele that carries the higher risk. C is at ~31% in Europeans. The same allele lowers risk among BRCA1 and BRCA2 mutation carriers, without affecting their breast-cancer risk.',
      },
      {
        rsid: 'rs10088218',
        gene: 'LINC00824 (8q24.21)',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'protective',
        effect:
          'An 8q24 gene-desert variant, well distal to MYC. Each A copy is associated with roughly 0.84x the odds of epithelial ovarian cancer and about 0.76x for serous tumours. A is uncommon — around 10% in European-, South-Asian- and African-ancestry groups, and under 1% in East Asians.',
      },
    ],
  },
  {
    id: 'glioma',
    name: 'Glioma (common variants)',
    aliases: ['brain tumour', 'brain cancer', 'glioblastoma', 'TERT', 'RTEL1', 'CDKN2B'],
    category: 'cancer',
    impact: 'modest',
    summary:
      'Glioma has a handful of clean, repeatedly replicated common-variant signals, mostly in telomere-biology genes (TERT, RTEL1) and at the 9p21 CDKN2A/B tumour-suppressor locus. Even so they add up to a small share of an already small baseline risk.',
    caveat:
      'Small shifts on a rare disease — lifetime glioma risk is well under 1% — so a risk allele changes very little in absolute terms, and there is no screening test to act on. Arrays do not cover the high-penetrance causes of familial brain tumours (TP53 / Li-Fraumeni, NF1, the Lynch mismatch-repair genes). These effects are established in European-ancestry cohorts; other populations are much less studied.',
    sources: 'GWAS Catalog (EFO_0005543); Shete et al. 2009; Wrensch et al. 2009; Rajaraman et al. 2012; Walsh et al. 2014',
    markers: [
      {
        rsid: 'rs2736100',
        gene: 'TERT',
        alleles: ['C', 'A'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'An intronic telomerase variant; the C allele tracks with longer telomeres. Each C copy is associated with roughly 1.25-1.4x the odds of glioma, strongest for high-grade tumours. C is near 50% in Europeans. The same allele is associated with lung adenocarcinoma.',
      },
      {
        rsid: 'rs4977756',
        gene: 'CDKN2B-AS1 / ANRIL (9p21.3)',
        alleles: ['G', 'A'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'In the ANRIL non-coding RNA that sits over the CDKN2A/CDKN2B tumour suppressors. Each G copy is associated with roughly 1.24x the odds of glioma. G is at ~40% in Europeans and ~21% in East Asians. This is a distinct signal from the 9p21 coronary-artery-disease variants.',
      },
      {
        rsid: 'rs6010620',
        gene: 'RTEL1 (20q13.33)',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'RTEL1 is a helicase that maintains telomeres. Each G copy is associated with roughly 1.3x the odds of glioma. G is common — ~79% in Europeans, ~97% in African-ancestry groups and ~29% in East Asians — so it flags very little about any one person.',
      },
    ],
  },
  {
    id: 'codeine-tramadol',
    name: 'Codeine and tramadol response (CYP2D6)',
    aliases: ['codeine', 'tramadol', 'CYP2D6', 'opioid painkiller', 'poor metabolizer', 'ultrarapid metabolizer'],
    category: 'pharmacogenomic',
    impact: 'notable',
    summary:
      'Codeine and tramadol are prodrugs — CYP2D6 has to convert them into the molecules that actually relieve pain. Too little enzyme and the drug barely works; too much and morphine appears faster than expected. These markers catch some of the common reduced-function alleles and miss a great deal else.',
    caveat:
      "CYP2D6 cannot be properly typed from array SNPs, and this entry is the clearest example of why. The gene is duplicated, deleted, and fused with its neighbouring pseudogene in ways a fixed panel of single-base positions cannot see, so the ultrarapid-metabolizer state — the one behind the paediatric deaths that prompted the FDA contraindication — is completely invisible here. A 'no variant found' result is not reassurance. Prescribing decisions belong to a clinician working from a clinical-grade CYP2D6 assay, not from this.",
    sources: 'CPIC guideline for CYP2D6 and codeine/tramadol; PharmGKB CYP2D6 star-allele definitions; FDA 2017 contraindication of codeine and tramadol in children',
    markers: [
      {
        rsid: 'rs3892097',
        gene: 'CYP2D6',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          "The *4 allele (c.1846G>A on the gene's own minus strand), a splice-site change that abolishes enzyme activity from that copy. It is the most common non-functional CYP2D6 allele in European-ancestry populations at roughly 18-21% of alleles, and rare in East Asian ones (<1%).",
      },
      {
        rsid: 'rs1065852',
        gene: 'CYP2D6',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'c.100C>T (Pro34Ser). It marks the reduced-function *10 allele, which is carried on roughly 40-50% of chromosomes in East Asian populations. It is not specific: in Europeans it usually rides on the *4 haplotype instead, so on its own it does not identify a star allele.',
      },
      {
        rsid: 'rs28371725',
        gene: 'CYP2D6',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'The *41 allele, which reduces rather than abolishes activity by lowering the amount of correctly spliced enzyme. Carried on about 8-9% of European chromosomes and around 1-3% of East Asian ones.',
      },
      {
        rsid: 'rs776746',
        gene: 'CYP3A5',
        alleles: ['T', 'C'],
        effectAllele: 'T',
        // Scored as a trait, not a risk: no established effect on codeine or
        // tramadol, so it must not count toward this condition's risk hits.
        direction: 'trait',
        effect:
          "Not a CYP2D6 variant, and not scored here. Tramadol's secondary N-demethylation runs through CYP3A4 and CYP2B6 — not CYP3A5 — and codeine has no CYP3A5 route at all. No study has shown CYP3A5 genotype to change tramadol or codeine exposure, and CPIC's opioid guideline covers CYP2D6, OPRM1 and COMT only. Shown purely as a pointer to the tacrolimus entry, where this variant does have an established effect.",
      },
    ],
  },
  {
    id: 'abacavir-hypersensitivity',
    name: 'Abacavir hypersensitivity (HLA-B*57:01)',
    aliases: ['abacavir', 'Ziagen', 'HLA-B*57:01', 'HCP5', 'HIV medication', 'drug hypersensitivity'],
    category: 'pharmacogenomic',
    impact: 'clinical',
    summary:
      'Abacavir, an HIV antiretroviral, causes a severe systemic hypersensitivity reaction almost exclusively in people carrying HLA-B*57:01. Pre-prescription HLA-B*57:01 testing is standard of care and is one of the few pharmacogenomic tests that changed practice outright.',
    caveat:
      'This is a tag SNP, not the HLA type. It tracks HLA-B*57:01 well in European and Hispanic populations and poorly in African-ancestry ones, where the related HLA-B*57:03 allele is common and B*57:01 is not reliably captured — a negative call there carries little weight. Anyone starting abacavir receives a direct HLA-B*57:01 test regardless of what an array says. As with every drug-gene entry here, the array reads a handful of fixed positions and cannot type a star-allele gene such as CYP2D6 at all; the prescribing decision belongs to a clinician.',
    sources: 'CPIC guideline for HLA-B and abacavir; Mallal et al. 2008 (PREDICT-1, NEJM); Daly et al. 2009 (flucloxacillin DILI); GWAS Catalog',
    markers: [
      {
        rsid: 'rs2395029',
        gene: 'HCP5',
        alleles: ['T', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'The G allele sits on the HCP5 transcript inside the MHC and tags HLA-B*57:01 with roughly 99% accuracy in European and Hispanic samples. In the PREDICT-1 trial, prospective HLA-B*57:01 screening cut immunologically confirmed abacavir hypersensitivity from 2.7% to zero. The same allele tags the strongest known risk factor for flucloxacillin liver injury (odds ratio around 80).',
      },
    ],
  },
  {
    id: 'allopurinol-scar',
    name: 'Allopurinol severe skin reaction (HLA-B*58:01)',
    aliases: ['allopurinol', 'gout medication', 'HLA-B*58:01', 'Stevens-Johnson syndrome', 'SJS TEN', 'DRESS', 'PSORS1C1'],
    category: 'pharmacogenomic',
    impact: 'clinical',
    summary:
      'Allopurinol, the standard drug for preventing gout attacks, occasionally triggers Stevens-Johnson syndrome, toxic epidermal necrolysis, or DRESS. Nearly all of that risk sits with the HLA-B*58:01 allele, and it is concentrated in East and Southeast Asian populations.',
    caveat:
      'A tag SNP, and a population-specific one. The A allele is common in European and African-ancestry groups (roughly 12-14% of alleles) where HLA-B*58:01 is not, so a positive call outside East Asia is weak evidence; in Japanese samples the allele is rare and tracks the HLA type closely. Guidelines recommend direct HLA-B*58:01 typing before allopurinol in patients of Han Chinese, Thai, or Korean ancestry, and this is no substitute for it. An array reads only fixed single positions — it cannot type HLA or a star-allele gene such as CYP2D6 — and the prescribing decision belongs to a clinician.',
    sources: 'Tohkin et al. 2013 (Japanese allopurinol SCAR GWAS); Hung et al. 2005; ACR gout guideline (HLA-B*58:01 screening); PharmGKB; SNPedia',
    markers: [
      {
        rsid: 'rs9263726',
        gene: 'PSORS1C1',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'The A allele tags HLA-B*58:01. A Japanese genome-wide study of allopurinol-induced severe cutaneous adverse reactions reported an odds ratio of about 109 for this SNP — the same figure as for direct HLA-B*58:01 typing in that sample. In Han Chinese the two track closely (r-squared about 0.89); in Tibetan and Hui samples the correlation is much weaker.',
      },
    ],
  },
  {
    id: 'carbamazepine-hypersensitivity',
    name: 'Carbamazepine hypersensitivity (HLA-A*31:01)',
    aliases: ['carbamazepine', 'Tegretol', 'oxcarbazepine', 'HLA-A*31:01', 'anticonvulsant rash', 'DRESS', 'epilepsy drug'],
    category: 'pharmacogenomic',
    impact: 'clinical',
    summary:
      'Carbamazepine causes a rash in a small percentage of people who take it, and rarely something far worse — DRESS, Stevens-Johnson syndrome, toxic epidermal necrolysis. Two HLA types carry most of that risk: HLA-A*31:01 across several ancestries, and HLA-B*15:02 in Southeast Asian populations. Only the first is tagged here.',
    caveat:
      'Two hard limits. First, this is an A/T variant, so a strand flip in a consumer export cannot be resolved from the genotype call alone — the app flags it rather than guessing, and a flagged call should not be read as a result. Second, HLA-B*15:02, the allele behind carbamazepine SJS/TEN in Han Chinese, Thai, Malay, and some Indian populations, is not tagged in this entry at all; nothing here speaks to it. Direct HLA typing is what guidelines call for, an array cannot type HLA or star-allele genes such as CYP2D6, and the prescribing decision belongs to a clinician.',
    sources: 'McCormack et al. 2011 (NEJM, HLA-A*31:01 in Europeans); Ozeki et al. 2011 (Japanese CBZ cADR GWAS); CPIC guideline for HLA and carbamazepine; ClinVar (carbamazepine hypersensitivity)',
    markers: [
      {
        rsid: 'rs1061235',
        gene: 'HLA-A',
        alleles: ['A', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          "A 3' UTR variant that serves as a proxy for HLA-A*31:01. The European genome-wide study reported an odds ratio of 9.1 (95% CI 4.0-20.7) for carbamazepine hypersensitivity, moving absolute risk from roughly 5% to roughly 26% in carriers. Validation work put its sensitivity for HLA-A*31:01 at 100% and specificity at 95%, the false positives coming from an HLA-A*33:03 haplotype.",
      },
    ],
  },
  {
    id: 'thiopurines',
    name: 'Thiopurine sensitivity (TPMT and NUDT15)',
    aliases: ['azathioprine', 'mercaptopurine', '6-MP', 'thioguanine', 'TPMT', 'NUDT15', 'myelosuppression', 'leukopenia'],
    category: 'pharmacogenomic',
    impact: 'clinical',
    summary:
      'Azathioprine, mercaptopurine, and thioguanine are handled by TPMT and NUDT15. Low activity in either enzyme lets the active metabolites build up in bone marrow, and a standard dose can then cause severe, occasionally life-threatening myelosuppression. Both genes are routinely tested before treatment starts.',
    caveat:
      'An array cannot rule out TPMT or NUDT15 deficiency. Rarer loss-of-function alleles are not on the chip, and phasing — whether two TPMT variants sit on the same chromosome or on opposite ones — cannot be read from genotype calls, which is exactly what separates *3A from a *3B/*3C compound. Standard practice before thiopurines is clinical genotyping or a TPMT enzyme-activity assay. The same caution applies across this whole catalogue: star-allele genes like CYP2D6 are not typeable from array SNPs, and dosing decisions belong to a clinician.',
    sources: 'CPIC guideline for TPMT/NUDT15 and thiopurines; Yang et al. 2014 (NUDT15, Nature Genetics); PharmGKB; ClinVar',
    markers: [
      {
        rsid: 'rs1800460',
        gene: 'TPMT',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'TPMT *3B (Ala154Thr). On its own it is uncommon; in European-ancestry populations it almost always sits on the same chromosome as rs1142345, forming the *3A haplotype — the most common deficient TPMT allele there at roughly 3-5% of alleles.',
      },
      {
        rsid: 'rs1142345',
        gene: 'TPMT',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'TPMT *3C (Tyr240Cys). Alone it defines *3C, the most common deficient allele in African and East Asian populations; paired with rs1800460 on the same chromosome it marks *3A. One deficient copy calls for a reduced starting dose, two for a large reduction or a different drug entirely.',
      },
      {
        rsid: 'rs116855232',
        gene: 'NUDT15',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'NUDT15 *3 (Arg139Cys). In a study of roughly 1,000 Korean patients, carriers had about 36 times the odds of early thiopurine-induced leukopenia (sensitivity 89%, specificity 93%); in European-ancestry IBD patients the odds ratio was about 9.5. The allele reaches about 10% in East Asian and is common in Latino populations, is common in Latino populations (about 4-6%), but sits near 0.2-0.3% in Europeans and is close to absent in African ones.',
      },
    ],
  },
  {
    id: 'fluoropyrimidine-toxicity',
    name: '5-FU and capecitabine toxicity (DPYD)',
    aliases: ['fluorouracil', '5-FU', 'capecitabine', 'Xeloda', 'DPYD', 'DPD deficiency', 'chemotherapy toxicity'],
    category: 'pharmacogenomic',
    impact: 'clinical',
    summary:
      '5-fluorouracil and capecitabine are broken down by DPD, the enzyme DPYD encodes. Partial DPD deficiency turns a standard dose into an overdose — severe mucositis, neutropenia, diarrhoea, and occasionally death. Pre-treatment DPYD genotyping is recommended across Europe and increasingly elsewhere.',
    caveat:
      'Several other clinically relevant DPYD variants — c.1679T>G (*13), the c.1236G>A/HapB3 haplotype, and a set of variants specific to African-ancestry populations such as c.557A>G — are generally absent from consumer arrays, so nothing found here does not rule out DPD deficiency. c.2846A>T is also an A/T variant, so a strand flip cannot be resolved from the call alone and it is flagged rather than corrected. Anyone about to start 5-FU or capecitabine should have clinical DPYD genotyping or DPD phenotyping; as everywhere in this catalogue, array SNPs cannot type star-allele genes such as CYP2D6, and the dose decision belongs to an oncologist.',
    sources: 'CPIC guideline for DPYD and fluoropyrimidines; Meulendijks et al. 2015 (Lancet Oncology); Deenen et al. 2016 (JCO, prospective *2A dose reduction); ClinVar; PharmGKB',
    markers: [
      {
        rsid: 'rs3918290',
        gene: 'DPYD',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'DPYD *2A, a splice-donor variant that abolishes activity from that copy. Heterozygotes retain roughly half-normal DPD. In a prospective Dutch study, *2A heterozygotes on a standard dose experienced grade 3 or worse toxicity in about 73% of cases, versus about 28% when the dose was halved upfront. Carried by around 1 in 100 people of European ancestry and rarer elsewhere.',
      },
      {
        rsid: 'rs67376798',
        gene: 'DPYD',
        alleles: ['T', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'c.2846A>T (Asp949Val), a decreased-function variant. A meta-analysis of individual patient data from over 7,000 treated patients gave a relative risk of 3.02 (95% CI 2.22-4.10) for severe fluoropyrimidine toxicity. Frequency is well under 1% in most populations.',
      },
    ],
  },
  {
    id: 'ppi-response',
    name: 'Proton pump inhibitor response (CYP2C19)',
    aliases: ['omeprazole', 'esomeprazole', 'lansoprazole', 'PPI', 'heartburn medication', 'CYP2C19', 'H. pylori', 'acid reflux'],
    category: 'pharmacogenomic',
    impact: 'notable',
    summary:
      'Omeprazole and its relatives are cleared by CYP2C19. Slow metabolizers hold onto more drug and get stronger acid suppression from the same dose; fast metabolizers clear it before it has finished the job. The effect is most visible in H. pylori eradication rates.',
    caveat:
      "Only two CYP2C19 star alleles are read here. *3, which is a meaningful loss-of-function allele in East Asian populations, and several rarer ones are absent, so the metabolizer call can be wrong — particularly a 'normal metabolizer' result. These same two variants also drive clopidogrel and antidepressant response, which are separate entries and separate conversations. Array SNPs cannot properly type star-allele genes at all — CYP2D6 being the worst case — and any dose change belongs to a clinician.",
    sources: 'CPIC guideline for CYP2C19 and proton pump inhibitor dosing (Lima et al. 2020); PharmGKB; SNPedia',
    markers: [
      {
        rsid: 'rs4244285',
        gene: 'CYP2C19',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'protective',
        effect:
          "The *2 loss-of-function allele. Carriers reach higher PPI concentrations and, in H. pylori eradication studies, higher cure rates than normal metabolizers on the same dose — hence 'protective' here. The flip side: CPIC suggests considering a lower dose for poor metabolizers on maintenance therapy beyond about twelve weeks. Common everywhere, and most common in East Asian populations at roughly 28-30% of alleles.",
      },
      {
        rsid: 'rs12248560',
        gene: 'CYP2C19',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'The *17 increased-function allele. Carriers clear PPIs faster and are more likely to get incomplete acid suppression; CPIC suggests considering an increased dose for ultrarapid metabolizers. Around 20% of alleles in European and African-ancestry populations, and about 1% in East Asian ones.',
      },
    ],
  },
  {
    id: 'ssri-metabolism',
    name: 'SSRI antidepressant metabolism (CYP2C19 and CYP2D6)',
    aliases: ['SSRI', 'escitalopram', 'citalopram', 'sertraline', 'paroxetine', 'fluvoxamine', 'antidepressant', 'CYP2C19', 'CYP2D6'],
    category: 'pharmacogenomic',
    impact: 'notable',
    summary:
      'Most SSRIs are cleared by CYP2C19 (citalopram, escitalopram, sertraline) or CYP2D6 (paroxetine, fluvoxamine). Metabolizer status can shift blood levels several-fold at the same dose, which shows up as side effects at one end and thin response at the other. It explains part of the variation in how people do on these drugs, not most of it.',
    caveat:
      "CYP2D6 in particular cannot be properly typed from array SNPs: duplications, whole-gene deletions, and CYP2D6/CYP2D7 hybrids are invisible to a fixed panel, so ultrarapid metabolizers are missed entirely and a *4 call on its own can misclassify someone. CYP2C19 *3 and other rarer alleles are also absent. Beyond that, genotype is a small part of why an antidepressant works or does not. Never change, start, or stop a psychiatric medication on the basis of this — that is a clinician's decision, made with a clinical-grade panel if genotype is going to be used at all.",
    sources: 'CPIC guideline for CYP2D6/CYP2C19 and SSRI antidepressants (Hicks et al. 2015, 2023 update); PharmGKB; ClinVar',
    markers: [
      {
        rsid: 'rs4244285',
        gene: 'CYP2C19',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'The *2 loss-of-function allele. Poor metabolizers reach substantially higher escitalopram and citalopram concentrations; CPIC suggests a 50% lower starting dose or an alternative drug, partly because citalopram exposure tracks with QT prolongation.',
      },
      {
        rsid: 'rs12248560',
        gene: 'CYP2C19',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'The *17 increased-function allele. Ultrarapid metabolizers reach lower escitalopram and citalopram concentrations, and CPIC suggests considering an alternative if response is poor. Roughly 20% of alleles in European and African-ancestry populations, about 1% in East Asian ones.',
      },
      {
        rsid: 'rs3892097',
        gene: 'CYP2D6',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'The *4 non-functional allele, the most common one in European-ancestry populations at roughly 18-21% of alleles. Two copies means poor metabolism of paroxetine and fluvoxamine and higher concentrations at a standard dose; CPIC suggests a 50% reduction or a different drug.',
      },
    ],
  },
  {
    id: 'tacrolimus-dosing',
    name: 'Tacrolimus dose requirement (CYP3A5)',
    aliases: ['tacrolimus', 'Prograf', 'CYP3A5', 'transplant', 'immunosuppressant', 'calcineurin inhibitor'],
    category: 'pharmacogenomic',
    impact: 'notable',
    summary:
      'Tacrolimus, the mainstay immunosuppressant after solid-organ transplant, has a narrow therapeutic window. Whether CYP3A5 is expressed at all is the single largest genetic determinant of the dose needed to land inside it — and unusually for pharmacogenomics, one common variant decides it.',
    caveat:
      'Tacrolimus is always dosed to measured trough levels; genotype refines a starting dose and never replaces the blood monitoring. Other factors — CYP3A4 variants, co-medications, time since transplant — move exposure as well, and none of them are read here. As across this catalogue, an array types single fixed positions and cannot resolve star-allele genes such as CYP2D6 at all; every dosing decision belongs to the transplant team.',
    sources: 'CPIC guideline for CYP3A5 and tacrolimus dosing (Birdwell et al. 2015); PharmGKB; SNPedia',
    markers: [
      {
        rsid: 'rs776746',
        gene: 'CYP3A5',
        alleles: ['T', 'C'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          "The T allele is CYP3A5 *1, the expressed form; the more common C allele is *3, which creates a cryptic splice site and yields no working enzyme. Expressers reach roughly half the dose-adjusted blood concentration of non-expressers, so CPIC recommends a starting dose 1.5 to 2 times standard for them — 'risk' here means the risk of sitting below the therapeutic range on a standard dose. *1 is carried by most people of African ancestry, a substantial minority of East and South Asian ancestry, and only about 5-10% of Europeans.",
      },
    ],
  },
  {
    id: 'eye-color',
    name: 'Eye color (blue vs brown)',
    aliases: ['eye colour', 'blue eyes', 'brown eyes', 'green eyes', 'HERC2', 'OCA2'],
    category: 'trait',
    impact: 'trait',
    summary:
      'One intronic variant in HERC2 controls a switch that turns OCA2 pigment production up or down in the iris, and it accounts for roughly three-quarters of blue-versus-brown variation in European-ancestry groups. That is unusually strong for a common trait, but it still does not settle green, hazel, or the exact shade.',
    caveat:
      "Studied almost entirely in European-ancestry groups, where both alleles are common. Nearly everyone of East Asian or African ancestry carries A/A regardless of anything else, so the marker says little outside Europe. 'Risk' and 'protective' here are just the app's words for which way the reported allele points — neither eye color is a health finding, though lighter eyes track with greater sun sensitivity.",
    sources: 'GWAS Catalog (eye colour measurement); Eiberg et al. 2008 (Hum Genet); Sturm et al. 2008 (Am J Hum Genet); Duffy et al. 2007 (Am J Hum Genet)',
    markers: [
      {
        rsid: 'rs12913832',
        gene: 'HERC2',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'The single strongest pigmentation variant known. G/G is associated with blue eyes in the large majority of European-ancestry carriers and A/A with brown (OR ~8 for brown per A copy in one cohort); G also tracks with lighter skin and modestly higher melanoma odds (~1.13×).',
      },
      {
        rsid: 'rs1800407',
        gene: 'OCA2',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'R419Q. A modifier rather than a switch: among people with the light-eye HERC2 genotype, the T allele is associated with green or hazel rather than blue. Uncommon (~5-7% in Europeans) and rare elsewhere.',
      },
    ],
  },
  {
    id: 'red-hair',
    name: 'Red hair & sun sensitivity (MC1R)',
    aliases: ['red hair', 'ginger', 'MC1R', 'freckles', 'sunburn', 'melanoma'],
    category: 'dermatologic',
    impact: 'notable',
    summary:
      "MC1R shifts pigment production between dark eumelanin and red-yellow pheomelanin. Three loss-of-function 'R' alleles carry most of the red-hair signal in European-ancestry groups, and the same alleles are among the best-replicated melanoma risk variants.",
    caveat:
      'Red hair behaves close to recessive: it usually appears in people carrying two R alleles, while single copies more often show as fair skin, freckling, and poor tanning. These three variants are common in northern and western Europe and rare elsewhere; other MC1R variants not listed here also contribute. The skin-cancer association is real but modest — sun behaviour matters more than genotype.',
    sources: 'GWAS Catalog (hair colour, cutaneous melanoma); Valverde et al. 1995 (Nat Genet); Morgan et al. 2018 (Nat Commun, UK Biobank)',
    markers: [
      {
        rsid: 'rs1805007',
        gene: 'MC1R',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'R151C, the strongest of the R alleles. In UK Biobank each T copy carried roughly 11-12× the odds of red hair, ~4.4× the odds of heavy freckling, and ~1.5-1.6× the odds of cutaneous melanoma.',
      },
      {
        rsid: 'rs1805008',
        gene: 'MC1R',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          "R160W. A true 'R' allele but weaker than R151C on a like-for-like basis — about 4× the odds of red hair per T copy in UK Biobank against ~11× for R151C (the ~13× sometimes quoted is the estimate after conditioning on the other MC1R alleles, not the plain per-copy one) and associated with ~1.37× the odds of cutaneous melanoma.",
      },
      {
        rsid: 'rs1805009',
        gene: 'MC1R',
        alleles: ['G', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'D294H. The rarest of the three R alleles; associated with lighter pigmentation, more freckling, and ~1.48× the odds of cutaneous melanoma.',
      },
    ],
  },
  {
    id: 'hair-curl',
    name: 'Hair curl & thickness',
    aliases: ['curly hair', 'straight hair', 'hair texture', 'TCHH', 'EDAR', 'thick hair'],
    category: 'trait',
    impact: 'trait',
    summary:
      'Two unrelated variants shape hair in two different parts of the world: one near the trichohyalin gene straightens European hair a little, and one in EDAR gives East Asian hair its characteristic thickness and straightness. Neither is a curl predictor on its own — together they explain only a few percent of the variation.',
    caveat:
      'Each marker is informative in one ancestry and near-monomorphic in the other, so a call can be uninformative rather than reassuring. The often-quoted TCHH variant rs11803731 is an A/T site: strand orientation cannot be recovered from an A/T call alone, so the tightly linked G/A marker rs17646946 is used here instead. Benign trait — the risk/protective labels only mark which way the reported allele points.',
    sources: 'GWAS Catalog (hair morphology); Medland et al. 2009 (Am J Hum Genet); Eriksson et al. 2010 (PLoS Genet 6:e1000993); Fujimoto et al. 2008 (Hum Mol Genet); Kamberov et al. 2013 (Cell)',
    markers: [
      {
        rsid: 'rs17646946',
        gene: 'TCHH / TCHHL1',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'In LD with the trichohyalin missense variant rs11803731. Each A copy shifted self-reported curl about 0.29 points straighter on a six-point scale in 5,385 Europeans. Frequency ~15-20% in Europeans; essentially absent in East Asia.',
      },
      {
        rsid: 'rs3827760',
        gene: 'EDAR',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'V370A, one of the strongest hair-shape signals anywhere (p ~1e-119 for hair shape). G is associated with thicker, straighter hair shafts, and also with shovel-shaped incisors and more eccrine sweat glands. Frequency ~78-90% in East Asia, under 1% in most European-ancestry groups (~5% in Finns), and ~1% in South Asians and Africans.',
      },
    ],
  },
  {
    id: 'bitter-taste-ptc',
    name: 'Bitter taste (PTC / PROP)',
    aliases: ['PTC', 'bitter taste', 'supertaster', 'TAS2R38', 'brussels sprouts', 'phenylthiocarbamide'],
    category: 'trait',
    impact: 'trait',
    summary:
      'Three linked missense changes in the bitter receptor TAS2R38 travel together as two haplotypes: PAV (taster) and AVI (non-taster). Diplotype at these three sites explains most of the variation in how bitter phenylthiocarbamide tastes — one of the largest single-gene effects on any human perception.',
    caveat:
      "Strand orientation is the trap here: the three sites are read on the gene's own strand in most papers, where the taster haplotype spells C-C-G. On the dbSNP forward strand used by this catalog it spells G-G-C. Real-life vegetable preference is only loosely related — the receptor explains PTC paper strips far better than it explains dinner. Both haplotypes are common in most populations.",
    sources: 'Kim et al. 2003 (Science); Bufe et al. 2005 (Curr Biol); Ong et al. 2018 (Sci Rep, UK Biobank); GWAS Catalog (taste liking, beverage consumption)',
    markers: [
      {
        rsid: 'rs713598',
        gene: 'TAS2R38',
        alleles: ['C', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          "P49A. Forward-strand G is the Pro49 'taster' base; C is Ala49, part of the non-taster AVI haplotype. In UK Biobank the G allele tracked with lower liking of bitter foods and slightly lower alcohol intake.",
      },
      {
        rsid: 'rs1726866',
        gene: 'TAS2R38',
        alleles: ['G', 'A'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'A262V. Forward-strand G is Ala262, the taster base; A is Val262. In near-complete LD with the other two sites — the three carry one signal, not three.',
      },
      {
        rsid: 'rs10246939',
        gene: 'TAS2R38',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'V296I. Forward-strand C is Val296, the taster base; T is Ile296. Two AVI haplotypes (C-A-T on the forward strand) is the classic non-taster genotype, about 30% of European-ancestry people.',
      },
    ],
  },
  {
    id: 'cilantro-soapy',
    name: 'Cilantro tastes soapy',
    aliases: ['cilantro', 'coriander', 'soapy taste', 'OR6A2', 'herbs'],
    category: 'trait',
    impact: 'trait',
    summary:
      'A variant in a cluster of olfactory receptor genes, including OR6A2 which responds to the aldehydes that give cilantro its smell, is associated with reporting that cilantro tastes soapy. The association is genuine but very small — this is a nudge, not the explanation.',
    caveat:
      'Effect size is the whole story: the variant shifts the odds by about a fifth and accounts for well under 1% of the variation in cilantro preference. Culture and exposure predict cilantro dislike far better. Discovery and replication cohorts were both European-ancestry and self-reported; no independent GWAS replication outside that work.',
    sources: 'Eriksson et al. 2012 (Flavour 1:22, 14,604 discovery + 11,851 replication)',
    markers: [
      {
        rsid: 'rs72921001',
        gene: 'OR6A2 / OR10A2 cluster',
        alleles: ['C', 'A'],
        effectAllele: 'A',
        direction: 'protective',
        effect:
          'Each A copy was associated with about 0.81× the odds of reporting cilantro as soapy (p = 6.4e-9). A is carried by roughly a third of European-ancestry people, so most of both groups carry a copy either way.',
      },
    ],
  },
  {
    id: 'asparagus-anosmia',
    name: 'Asparagus urine anosmia',
    aliases: ['asparagus', 'asparagus pee', 'smell', 'anosmia', 'OR2M7', 'methanethiol'],
    category: 'trait',
    impact: 'trait',
    summary:
      'Nearly everyone produces the sulfur compounds in urine after eating asparagus; the split is in who can smell them. A variant beside a cluster of olfactory receptor genes on chromosome 1 is one of the cleanest single-locus smell associations known.',
    caveat:
      "The phenotype is self-reported ('have you ever noticed a peculiar odor?'), which mixes true anosmia with never having paid attention. The variant appears to act dominantly, so one copy carries most of the effect. Both the original and the replicating study were in European-ancestry cohorts. Benign trait — the labels just mark which way the reported allele points.",
    sources: 'Eriksson et al. 2010 (PLoS Genet 6:e1000993, 4,742 participants); Markt et al. 2016 (BMJ 355:i6071, ~6,900 participants)',
    markers: [
      {
        rsid: 'rs4481887',
        gene: 'OR2M7 (1q44 olfactory cluster)',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'protective',
        effect:
          'Each A copy was associated with roughly 0.60× the odds of being unable to smell the odor (0.48× under a dominant model) — In the discovery cohort 71-74% of A carriers reported smelling it against 57% of G/G homozygotes — the allele shifts the odds, it does not sort people into two groups. A frequency ~20-25% in Europeans.',
      },
    ],
  },
  {
    id: 'photic-sneeze',
    name: 'Photic sneeze reflex (ACHOO)',
    aliases: ['photic sneeze', 'sun sneeze', 'ACHOO', 'sneezing at bright light', 'ZEB2'],
    category: 'trait',
    impact: 'trait',
    summary:
      'Sneezing when stepping into bright sunlight runs in families and behaves roughly like a dominant trait. One common variant at 2q22.3, near ZEB2, is the only well-established genetic association — and it moves the odds by about a third, so most of the reason remains unexplained.',
    caveat:
      'Both studies used self-report, and the effect is small relative to how strongly the trait clusters in families — this SNP is a marker, not the cause. A smaller Chinese GWAS labelled the effect allele the other way around, but the risk-allele frequency it reported (0.44) matches C, not T, in East Asian reference panels. Benign, though worth knowing before driving out of a tunnel.',
    sources: 'Eriksson et al. 2010 (PLoS Genet 6:e1000993, 5,390 European-ancestry participants); Sci Rep 2019 (874 cases / 2,543 controls, Chinese cohort, PMID 30899065)',
    markers: [
      {
        rsid: 'rs10427255',
        gene: '2q22.3 (near ZEB2)',
        alleles: ['C', 'T'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'Each C copy was associated with about 1.32× the odds of the photic sneeze reflex in European-ancestry participants; a Chinese cohort found the same locus with a somewhat larger effect. C is common everywhere (~42-46%).',
      },
    ],
  },
  {
    id: 'height-polygenic',
    name: 'Height (a polygenic example)',
    aliases: ['height', 'tall', 'short', 'stature', 'polygenic', 'GDF5', 'HMGA2'],
    category: 'trait',
    impact: 'trait',
    summary:
      'Height is about 80% heritable and is the standard demonstration of a polygenic trait: the 2022 GIANT/UK Biobank analysis needed roughly 12,000 near-independent variants to capture ~40% of the variance in European-ancestry adults. The two markers below are among the largest single-variant effects known, and each still moves the average by well under a centimetre.',
    caveat:
      'Included as an honest illustration of what a polygenic trait looks like, not as a prediction — two SNPs out of twelve thousand explain a fraction of a percent between them, and childhood nutrition can outweigh them entirely. Variance explained drops sharply in non-European-ancestry groups because the discovery cohorts were overwhelmingly European. Neither direction is good or bad; the labels only mark the reported allele.',
    sources: 'Yengo et al. 2022 (Nature, 5.4M individuals); Weedon et al. 2007 (Nat Genet, HMGA2); Sanna et al. 2008 (Nat Genet, GDF5-UQCC); GWAS Catalog (body height)',
    markers: [
      {
        rsid: 'rs143384',
        gene: 'GDF5',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'One of the largest common height effects: each A copy is associated with roughly 0.06-0.08 standard deviations shorter stature (about half a centimetre), p < 1e-300. The same allele is associated with higher osteoarthritis and knee-pain risk.',
      },
      {
        rsid: 'rs1042725',
        gene: 'HMGA2',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'The first common height variant ever found. Each T copy is associated with roughly 0.4-0.5 cm shorter adult height, and with slightly lower birth weight and head circumference.',
      },
    ],
  },
  {
    id: 'hcv-clearance',
    name: 'Hepatitis C clearance & interferon response (IFNL4)',
    aliases: ['hepatitis C', 'HCV', 'IL28B', 'IFNL4', 'interferon lambda', 'interferon response'],
    category: 'pharmacogenomic',
    impact: 'notable',
    summary:
      'Variation upstream of IFNL4 (long labelled IL28B) is the strongest known host predictor of what happens after hepatitis C infection: whether the virus clears on its own, and how well the old interferon-based regimens worked. It was one of the first genuinely useful pharmacogenomic GWAS findings.',
    caveat:
      'Largely historical for treatment: modern direct-acting antivirals cure well over 95% of patients regardless of genotype, so this no longer guides therapy. The causal variant is a two-base frameshift (rs368234815) that this catalog cannot represent; rs12979860 tags it almost perfectly in European and East Asian groups but less well in African-ancestry groups, which is part of why response rates differed by ancestry. It says nothing about catching HCV — only about what follows.',
    sources: 'Ge et al. 2009 (Nature); Thomas et al. 2009 (Nature); Tanaka et al. 2009 (Nat Genet); Suppiah et al. 2009 (Nat Genet); PharmGKB (IFNL3/IFNL4)',
    markers: [
      {
        rsid: 'rs12979860',
        gene: 'IFNL4 (IL28B region)',
        alleles: ['C', 'T'],
        effectAllele: 'C',
        direction: 'protective',
        effect:
          'C/C was associated with roughly 2-3× the rate of spontaneous clearance after acute infection and about twice the sustained virologic response to peginterferon plus ribavirin. C frequency is ~65% in Europeans, ~90% in East Asians, and lower in African-ancestry groups.',
      },
      {
        rsid: 'rs8099917',
        gene: 'IFNL4 (IL28B region)',
        alleles: ['T', 'G'],
        effectAllele: 'T',
        direction: 'protective',
        effect:
          'The tag used in the Japanese and Australian discovery studies. G carriers had roughly 2-3× the odds of chronic infection and interferon non-response; T is the favorable allele. Best-performing tag in East Asian groups, weaker than rs12979860 in Europeans. Array coverage varies.',
      },
    ],
  },
  {
    id: 'covid-severity',
    name: 'COVID-19 severity',
    aliases: ['COVID', 'COVID-19', 'SARS-CoV-2', 'coronavirus', 'LZTFL1', 'blood group'],
    category: 'respiratory',
    impact: 'modest',
    summary:
      'Two loci came out of the international host-genetics effort: a chromosome 3 haplotype inherited from Neanderthals, which is the largest common genetic risk factor for severe COVID-19, and the ABO blood-group locus, which nudges the odds of being infected at all.',
    caveat:
      'Age, vaccination status, and comorbidity dominate these effects by a wide margin, and the estimates come from largely unvaccinated pre-Omicron cohorts, so they may not carry forward. Frequency of the chromosome 3 risk allele varies enormously by ancestry — roughly 8% in Europeans, around 30% in South Asians, and near zero in East Asians — which is why cross-population comparisons of this variant mislead easily. The functional ABO variant is a deletion this catalog cannot represent; rs505922 is only a tag for it.',
    sources: 'COVID-19 Host Genetics Initiative (Nature 2021); Zeberg & Pääbo 2020 (Nature); Nakanishi et al. 2021 (J Clin Invest); GWAS Catalog (COVID-19)',
    markers: [
      {
        rsid: 'rs10490770',
        gene: 'LZTFL1 (3p21.31)',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'Lead marker for the Neanderthal-derived 3p21.31 haplotype. Each C copy was associated with roughly 1.6-1.9× the odds of critical illness or hospitalization and ~1.16× the odds of infection; the effect was strongest in people under 60.',
      },
      {
        rsid: 'rs505922',
        gene: 'ABO',
        alleles: ['C', 'T'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'Tags non-O blood groups. C was associated with ~1.10× the odds of COVID-19 with respiratory failure, and T (tagging O) with ~0.90-0.93× the odds of reported infection. The same C allele is associated with roughly 1.8× the odds of venous thromboembolism, a much larger effect than its COVID one.',
      },
    ],
  },
  {
    id: 'g6pd-deficiency',
    name: 'G6PD deficiency (A− variant)',
    aliases: ['G6PD', 'favism', 'hemolytic anemia', 'fava beans', 'primaquine', 'malaria'],
    category: 'hematologic',
    impact: 'clinical',
    summary:
      'G6PD protects red cells against oxidative stress. The African A− allele leaves roughly 10-30% of normal enzyme activity — usually silent until an oxidative trigger such as fava beans, primaquine or tafenoquine, rasburicase, dapsone, or a severe infection causes acute hemolysis. It is the most common enzyme deficiency in the world, and its distribution follows historical malaria.',
    caveat:
      'X-linked, which changes how a genotype file should be read: males carry one copy, so an array export may show a single letter or a doubled letter on chromosome X and copy counting can mislead. Heterozygous females are mosaics with intermediate, unpredictable enzyme levels — genotype alone does not tell you their risk. This entry covers only the A− variant common in African-ancestry groups; the Mediterranean, Canton, and Mahidol variants cause more severe deficiency and are not typed here, so a normal call is not a clearance. Any real concern calls for an enzyme activity assay, not an array.',
    sources: 'ClinVar (rs1050828, pathogenic); CPIC guideline (rasburicase / G6PD); Clarke et al. 2017 (eLife, MalariaGEN); Wheeler et al. 2017 (PLoS Med, HbA1c)',
    markers: [
      {
        rsid: 'rs1050828',
        gene: 'G6PD',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'c.202G>A (Val68Met), the variant defining the A− deficiency allele — A− is a haplotype, not a single SNP: c.202G>A sitting in cis with c.376A>G (rs1050829), which is not typed here. Carried by carried by roughly 10-15% of men of African ancestry and rare elsewhere. It also lowers measured HbA1c by about 0.8 percentage points in hemizygous men and about 0.7 in homozygous women (roughly 0.3 in heterozygous women), which can hide diabetes on a routine test, and large African cohort studies link it to reduced cerebral malaria alongside increased severe malarial anaemia.',
      },
    ],
  },
  {
    id: 'psoriasis',
    name: 'Psoriasis',
    aliases: ['plaque psoriasis', 'psoriasis vulgaris', 'scaly skin', 'HLA-C*06:02', 'PSORS1', 'IL23R', 'skin', 'autoimmune skin disease'],
    category: 'dermatologic',
    impact: 'notable',
    summary:
      'Psoriasis is immune overactivity rather than a broken skin barrier. The IL-23/Th17 signalling axis drives keratinocytes into runaway proliferation, which is what produces thick, silvery-scaled plaques on an otherwise competent barrier. That is close to the mechanistic opposite of atopic dermatitis, which starts with a leaky barrier (filaggrin loss) and adds Th2 allergic inflammation on top. The two conditions look alike from a distance — red, itchy, inflamed skin — and they do share genetic loci, but the shared alleles often push in opposite directions: the TNIP1 variant below raises psoriasis risk while lowering atopic dermatitis risk. Shared DNA, opposing effects — which is part of why the two respond to different drug classes. Psoriasis risk is dominated by one locus, HLA-C*06:02 in the MHC; everything found since is much smaller.',
    caveat:
      'The HLA-C marker below is a tag SNP, not an HLA type. Roughly 10-15% of people of European ancestry carry the tagged haplotype and the large majority never develop psoriasis, so a positive tag is weakly predictive on its own. Effect sizes here come mostly from European-ancestry cohorts; the HLA-C*06:02 association replicates in East Asian populations but the frequencies differ.',
    sources: 'GWAS Catalog (MONDO_0005083); Nair et al. 2009 Nat Genet; Tsoi et al. 2012 Nat Genet; Strange et al. 2010 Nat Genet; dbSNP (GRCh38, plus strand)',
    markers: [
      {
        rsid: 'rs10484554',
        gene: 'HLA-C / PSORS1',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'Tags the HLA-C*06:02 haplotype, the strongest psoriasis locus known. European-ancestry studies report roughly 2.8-3.3× the odds of psoriasis per T copy, with the association strongest for early-onset plaque psoriasis. The tag captures the haplotype imperfectly, not the HLA allele itself.',
        note:
          'Present on 23andMe v1-v5 and AncestryDNA arrays.',
      },
      {
        rsid: 'rs11209026',
        gene: 'IL23R',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'protective',
        effect:
          "R381Q, in the receptor for IL-23. Studies report about 1.5× the odds of psoriasis for the common G allele, so the A allele sits at roughly 0.67× — a protective direction. The same A allele is protective against Crohn's disease, ulcerative colitis and ankylosing spondylitis. One shared IL-23 axis across four diseases, which is why a single drug class (IL-23 inhibitors) works across all of them.",
        note:
          'Uncommon: about 5-7% frequency in Europeans, and rarer still in East Asian and African populations.',
      },
      {
        rsid: 'rs17728338',
        gene: 'TNIP1',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'Near TNIP1, a brake on NF-κB inflammatory signalling. The minor A allele (~5-9% in Europeans) is associated with roughly 1.6× the odds of psoriasis — the largest non-MHC effect outside IL23R.',
        note:
          'Present on 23andMe v3-v5.',
      },
    ],
  },
  {
    id: 'atopic-dermatitis',
    name: 'Atopic dermatitis (eczema)',
    aliases: ['eczema', 'atopic eczema', 'atopic dermatitis', 'AD', 'filaggrin', 'FLG', 'dry skin', 'itchy skin', 'atopy', 'allergic skin'],
    category: 'dermatologic',
    impact: 'notable',
    summary:
      "Atopic dermatitis begins as a barrier problem, not an immune one. Filaggrin is the protein that builds and cross-links the tough outer layer of skin; when a loss-of-function variant knocks out one or both copies, water escapes and allergens get in, and Th2 allergic inflammation follows through the breach. Psoriasis runs the other way round — IL-23/Th17 immune overactivity driving an intact barrier into overdrive. That mechanistic split does not mean separate genetics: the comparative study of the two diseases (Baurecht et al. 2015) found shared loci where the same allele pushes in opposite directions, which is a different and more interesting thing than non-overlap. Filaggrin also explains the 'atopic march': a leaky skin barrier in infancy predisposes to food allergy, asthma and hay fever later.",
    caveat:
      "Filaggrin's two common European null alleles are R501X (the SNP below) and 2282del4, a four-base deletion this catalog cannot type. A negative R501X result therefore does not rule out filaggrin deficiency — it rules out one of at least two common ways to lose it. FLG null alleles are also strongly population-specific: R501X and 2282del4 are the European set, while East Asian cohorts carry a largely different collection of null variants that consumer arrays do not cover.",
    sources: 'GWAS Catalog (MONDO_0004980); Palmer et al. 2006 Nat Genet; Paternoster et al. 2015 Nat Genet; EAGLE eczema consortium; Esparza-Gordillo et al. 2009 Nat Genet; dbSNP (GRCh38, plus strand)',
    markers: [
      {
        rsid: 'rs61816761',
        gene: 'FLG',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'R501X. The A allele introduces a premature stop codon and abolishes filaggrin from that copy of the gene. It is the single largest known common-variant effect in atopic dermatitis — around 2.6× the odds per copy in European-ancestry meta-analyses. People carrying null alleles on both copies usually have ichthyosis vulgaris: persistently dry, scaly skin. Carried by roughly 2-4% of Europeans and essentially absent in East Asian and African populations.',
        note:
          'A simple point mutation, unlike FLG 2282del4. Also associated with childhood-onset asthma (~2× odds), but largely in people who have eczema first. Present on 23andMe v4 and AncestryDNA.',
      },
      {
        rsid: 'rs20541',
        gene: 'IL13',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'R130Q, a coding change in the Th2 cytokine IL-13. The A allele is associated with higher serum IgE and roughly 1.17× the odds of eczema, and independently with asthma and allergic sensitisation. This is the allergy half of the disease rather than the barrier half.',
        note:
          'SNPedia reports this SNP on the minus strand as C/T; the plus-strand pair is A/G and the risk allele A corresponds to minus-strand T.',
      },
      {
        rsid: 'rs7927894',
        gene: '11q13.5 (EMSY / LRRC32)',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          "An intergenic variant at 11q13.5, one of the first non-filaggrin eczema loci to reach genome-wide significance. Reported at roughly 1.1-1.2× the odds per T copy. The same allele is associated with asthma and with Crohn's disease.",
        note:
          "Some papers report this variant on the opposite strand as 'A' — that is the same allele, not a contradiction.",
      },
      {
        rsid: 'rs479844',
        gene: 'OVOL1',
        alleles: ['A', 'G'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'Near OVOL1, a transcription factor that governs keratinocyte differentiation — a barrier gene, in the same broad category as filaggrin. The G allele is associated with roughly 1.1-1.25× the odds of eczema. Found in both East Asian and European cohorts, which is unusual for eczema loci.',
        note:
          'SNPedia lists this on the minus strand as C/T; plus-strand G corresponds to minus-strand C.',
      },
    ],
  },
  {
    id: 'psoriatic-arthritis',
    name: 'Psoriatic arthritis',
    aliases: ['PsA', 'psoriasis arthritis', 'inflammatory arthritis', 'spondyloarthritis', 'TRAF3IP2', 'joint inflammation'],
    category: 'autoimmune',
    impact: 'notable',
    summary:
      'Psoriatic arthritis is inflammatory joint and entheseal disease in people who have psoriasis — it develops in roughly 20-30% of them, usually years after the skin disease. Genetically it is mostly psoriasis, so the markers below deliberately overlap with that entry. The handful of signals that differ point at the joints rather than the skin: HLA-B*27, which consumer arrays cannot type, and a TRAF3IP2 variant whose effect is noticeably larger for arthritis than for plaques. Like psoriasis, the underlying biology is IL-23/Th17 immune overactivity.',
    caveat:
      'Nothing here separates psoriatic arthritis from psoriasis with any useful accuracy. The shared loci dominate, and the one marker that genuinely discriminates axial disease — HLA-B*27 — is not on consumer arrays. Diagnosis is clinical, made by a rheumatologist on examination and imaging.',
    sources: 'GWAS Catalog (MONDO_0011849); Hüffmeier et al. 2010 Nat Genet; Bowes et al. 2015 Nat Commun; Liu et al. 2008 PLoS Genet; dbSNP (GRCh38, plus strand)',
    markers: [
      {
        rsid: 'rs33980500',
        gene: 'TRAF3IP2',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'D10N in Act1, the adaptor protein that carries IL-17 receptor signals. The T allele (~7-8% in Europeans) is associated with roughly 1.95× the odds of psoriatic arthritis and about 1.4× for skin psoriasis — one of the few loci that reads stronger in the joints than in the skin.',
        note:
          'Present on 23andMe v5 and AncestryDNA; not on earlier 23andMe chips.',
      },
      {
        rsid: 'rs10484554',
        gene: 'HLA-C / PSORS1',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'The HLA-C*06:02 tag, shared with psoriasis (roughly 2.8-3.3× odds for psoriasis per T copy). It is associated with psoriatic arthritis largely by way of the skin disease it predisposes to, rather than as an independent joint signal.',
        note:
          'A tag SNP for the haplotype, not an HLA type. Present on 23andMe v1-v5 and AncestryDNA.',
      },
      {
        rsid: 'rs11209026',
        gene: 'IL23R',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'protective',
        effect:
          "R381Q. The A allele reduces risk across the whole IL-23 axis — psoriasis, Crohn's disease, ulcerative colitis and ankylosing spondylitis all show the same allele in the same protective direction, at roughly 0.4-0.7× the odds depending on the disease. That common axis is why one drug class, IL-23 inhibitors, treats all of them including psoriatic arthritis.",
        note:
          "Honest caveat on the evidence: the genome-wide significant, replicated associations for this exact SNP are in psoriasis, Crohn's, ulcerative colitis and ankylosing spondylitis. The psoriatic-arthritis evidence for the IL23R locus rests on smaller targeted studies and on nearby variants such as rs2201841.",
      },
    ],
  },
  {
    id: 'asthma',
    name: 'Asthma',
    aliases: ['asthma', 'wheeze', 'bronchial asthma', 'childhood asthma', 'ORMDL3', 'GSDMB', '17q21', 'airway', 'atopy', 'allergic asthma'],
    category: 'respiratory',
    impact: 'modest',
    summary:
      'Asthma is not one disease genetically. Childhood-onset asthma carries a strong, exceptionally well-replicated signal at 17q21 (GSDMB/ORMDL3) that barely registers in adult-onset disease. Childhood-onset disease also carries most of the allergy and Th2 architecture shared with eczema and hay fever — FLG, IL33, TSLP, IL1RL1, IL13 — where the effects are consistently larger than in adults, and its SNP heritability is roughly three times that of adult-onset disease. Adult-onset asthma has almost no loci of its own: fewer signals, smaller effects, and a more lung-centred, environmentally driven picture. The markers below are the largest common-variant signals found, but each moves the odds only a little, and none is diagnostic.',
    caveat:
      'Asthma risk is dominated by environment and timing — respiratory infections in infancy, allergen and tobacco-smoke exposure, air quality, occupation. The 17q21 effect in particular has been reported to depend on early-life exposures, so genotype read alone is a poor guide. These are population-level associations, not a personal forecast, and most were established in European-ancestry cohorts.',
    sources: 'GWAS Catalog (MONDO_0004979); Moffatt et al. 2007 Nature; Moffatt et al. 2010 NEJM (GABRIEL); Demenais et al. 2018 Nat Genet (TAGC); Ferreira et al. 2017 Nat Genet; dbSNP (GRCh38, plus strand)',
    markers: [
      {
        rsid: 'rs7216389',
        gene: 'GSDMB / ORMDL3 (17q21)',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'The 17q21 locus — the strongest and most reproduced common signal in childhood asthma. Studies report roughly 1.45× the odds of childhood-onset asthma per T copy, and the allele tracks with higher ORMDL3 expression. The allele is common (about 50-60% in Europeans), and the effect in adult-onset asthma is much weaker.',
        note:
          'Present on 23andMe v1-v5 and AncestryDNA.',
      },
      {
        rsid: 'rs1342326',
        gene: 'IL33',
        alleles: ['A', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'Upstream of IL33, an alarmin released by damaged airway lining that switches on Th2 inflammation. The minor C allele (~16%) is associated with roughly 1.2× the odds of asthma and with more frequent exacerbations. IL-33 and its receptor IL1RL1/ST2 are both active drug targets.',
        note:
          'SNPedia lists this on the minus strand as G/T; the plus-strand pair is A/C and the risk allele C corresponds to minus-strand G.',
      },
      {
        rsid: 'rs1837253',
        gene: 'TSLP',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'Near TSLP, another airway-epithelium alarmin and the target of tezepelumab. The common C allele (~74% in Europeans) is associated with about 1.1-1.2× the odds of asthma, in both childhood- and adult-onset forms; the T allele is the protective direction.',
        note:
          'Present on 23andMe v1-v5 and AncestryDNA.',
      },
      {
        rsid: 'rs20541',
        gene: 'IL13',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'R130Q, in the Th2 cytokine IL-13. The A allele raises serum IgE and is associated with roughly 1.12× the odds of asthma. The same allele in the same direction shows up in atopic dermatitis and allergic rhinitis — part of why the three conditions cluster in families.',
        note:
          'SNPedia reports this SNP on the minus strand as C/T; the plus-strand pair is A/G.',
      },
    ],
  },
  {
    id: 'vitiligo',
    name: 'Vitiligo',
    aliases: ['depigmentation', 'white patches', 'leukoderma', 'TYR', 'PTPN22', 'autoimmune skin'],
    category: 'dermatologic',
    impact: 'modest',
    summary:
      'An autoimmune condition in which pigment cells in the skin are destroyed, leaving pale patches. Around 50 loci have reached genome-wide significance; most are immune genes, but the strongest common signal sits in a pigmentation gene. Lifetime prevalence is roughly 0.5-1%, so even a doubled relative risk leaves absolute risk low.',
    caveat:
      'The largest vitiligo signals are in the HLA region, which consumer arrays cover poorly. The markers below are among the strongest non-HLA hits but capture a small share of heritability, and most of the evidence comes from European-ancestry cohorts.',
    sources: 'GWAS Catalog (EFO_0004208); Jin et al. 2010 NEJM 362:1686; Jin et al. 2016 Nat Genet 48:1418',
    markers: [
      {
        rsid: 'rs1126809',
        gene: 'TYR',
        alleles: ['G', 'A'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          'Arg402Gln in tyrosinase, the rate-limiting enzyme of melanin synthesis. The common G (Arg402) allele is associated with roughly 1.5× the odds of generalized vitiligo in European-ancestry cohorts (p≈1e-43). The same allele frequency is about 73% in those cohorts.',
        note:
          'This is the reverse of the melanoma story: the A (402Gln) allele that goes with fairer skin and higher melanoma risk is the protective allele for vitiligo. One proposed explanation is that the low-activity 402Gln enzyme presents less tyrosinase antigen to the immune system.',
      },
      {
        rsid: 'rs2476601',
        gene: 'PTPN22',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'R620W, the same general-autoimmunity variant seen in rheumatoid arthritis and type 1 diabetes. Each A copy is associated with roughly 1.4× the odds of vitiligo (p≈1e-18, risk-allele frequency about 10%).',
        note:
          'Rare in East Asian populations, so this marker carries little information outside European-ancestry groups.',
      },
      {
        rsid: 'rs11203203',
        gene: 'UBASH3A',
        alleles: ['G', 'A'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'A T-cell signalling regulator shared with other autoimmune conditions. Each A copy is associated with roughly 1.3× the odds of vitiligo (p≈1e-09).',
      },
    ],
  },
  {
    id: 'sun-sensitivity',
    name: 'Sun sensitivity and freckling',
    aliases: ['freckles', 'sunburn', 'MC1R', 'red hair', 'skin type', 'photosensitivity', 'tanning', 'IRF4'],
    category: 'dermatologic',
    impact: 'notable',
    summary:
      'How readily skin burns rather than tans, and how much it freckles, is one of the few common human traits where single common variants have large effects. MC1R controls the switch between red/yellow pheomelanin and brown/black eumelanin; loss-of-function variants push pigment toward pheomelanin, which protects poorly against UV. These are also the best-replicated common variants for keratinocyte carcinoma and melanoma risk.',
    caveat:
      'MC1R carries several other red-hair variants (D294H, R142H, V60L and others) that arrays cover unevenly, so a person can be sun-sensitive on MC1R grounds these three markers miss entirely. All the effect sizes below come from European-ancestry cohorts, where these alleles are common; they are rare elsewhere and say little about pigmentation in other ancestry groups. Sun protection advice does not depend on genotype.',
    sources: 'GWAS Catalog (MONDO_0005434 skin sensitivity to sun, EFO_0003963 freckles); Sulem et al. 2007 Nat Genet 39:1443; Han et al. 2008 PLoS Genet 4:e1000074; UK Biobank sunburn/skin-cancer analyses',
    markers: [
      {
        rsid: 'rs1805007',
        gene: 'MC1R',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'R151C, one of the three classic MC1R red-hair variants. Reported at roughly 4.4× the odds of freckling and 2.9× the odds of sun-sensitive skin, with about 11-12× the odds of red hair in carriers of two red-hair alleles. Also about 1.3-1.5× the odds of basal-cell and squamous-cell carcinoma per copy.',
        note:
          'About 6-8% allele frequency in European-ancestry cohorts, near-absent elsewhere. Effects on hair colour are strongest when two MC1R variant alleles are inherited together.',
      },
      {
        rsid: 'rs1805008',
        gene: 'MC1R',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'R160W, a second classic red-hair variant sitting 27 bases from R151C. Similar magnitude: roughly 12-13× the odds of red hair, and about 1.2× the odds of keratinocyte carcinoma and 1.4× the odds of cutaneous melanoma per copy.',
        note:
          "Because R151C and R160W sit so close together, they are usually inherited on separate haplotypes; carrying one of each is the common 'compound' red-hair genotype.",
      },
      {
        rsid: 'rs12203592',
        gene: 'IRF4',
        alleles: ['C', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'An intronic regulatory variant that alters IRF4 expression in melanocytes. The T allele is strongly associated with freckling (p≈2e-91), lighter hair, easier sunburn, and increased squamous-cell carcinoma risk.',
        note:
          'About 16-20% allele frequency in European-ancestry groups and much rarer in East Asian and African-ancestry groups. Independent of MC1R.',
      },
    ],
  },
  {
    id: 'allergic-rhinitis',
    name: 'Allergic rhinitis / hay fever',
    aliases: ['hay fever', 'seasonal allergies', 'pollen allergy', 'atopy', 'TSLP', 'allergic disease', 'sneezing'],
    category: 'respiratory',
    impact: 'modest',
    summary:
      "Hay fever shares most of its genetic architecture with asthma and eczema — the 'atopic' cluster. Around 40 loci have reached genome-wide significance in combined allergic-disease studies; almost all are immune-regulatory, and almost all have small effects. The two below are among the few discovered against allergic rhinitis specifically rather than asthma alone.",
    caveat:
      'Effect sizes here are small — odds ratios near 1.2 per allele against a background lifetime prevalence of 20-30% in many populations. Genotype adds very little on top of family history and childhood exposure history. Both markers are shared with asthma, eczema and inflammatory bowel disease, so neither is specific to hay fever.',
    sources: 'GWAS Catalog (EFO_0005854 seasonal allergic rhinitis, EFO_0003785 allergic disease); Ramasamy et al. 2011 J Allergy Clin Immunol 128:996; Ferreira et al. 2017 Nat Genet 49:1752',
    markers: [
      {
        rsid: 'rs1837253',
        gene: 'TSLP',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'Just upstream of thymic stromal lymphopoietin, an epithelial alarmin that starts the type-2 allergic cascade. The C allele is associated with roughly 1.17× the odds of seasonal allergic rhinitis (p≈1e-09) and about 1.07× the odds of allergic disease broadly (p≈3e-19); it also raises blood eosinophil counts.',
        note:
          'C is the common allele, at about 74% in European-ancestry cohorts, so most people carry one or two copies. TSLP is the target of the asthma biologic tezepelumab.',
      },
      {
        rsid: 'rs2155219',
        gene: 'EMSY / LRRC32 (11q13.5)',
        alleles: ['G', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'An intergenic variant in the 11q13.5 atopy locus. The T allele is associated with roughly 1.17-1.22× the odds of seasonal allergic rhinitis (p≈1e-08 to 4e-08) and about 1.18× the odds of allergic sensitisation on skin-prick or IgE testing.',
        note:
          'Close to a 50/50 allele split in most populations. The same allele is associated with eczema, childhood asthma, alopecia areata and ulcerative colitis — a general barrier-and-immune-regulation signal rather than a hay-fever-specific one.',
      },
    ],
  },
  {
    id: 'androgenetic-alopecia',
    name: 'Male pattern baldness',
    aliases: ['androgenetic alopecia', 'hair loss', 'balding', 'receding hairline', 'AR', 'EDA2R', '20p11'],
    category: 'dermatologic',
    impact: 'trait',
    summary:
      'The most heritable common trait in this catalog after pigmentation — twin studies put heritability around 80%. Two loci dominate: the androgen receptor region on the X chromosome, which is why the trait tracks the maternal line in family lore, and an independent region on chromosome 20p11. Large biobank studies have since found several hundred more loci, each tiny.',
    caveat:
      'Baldness is benign, and nothing here is a medical finding. Even with the two largest loci, genotype predicts pattern and timing poorly at the individual level — biobank scores built on hundreds of variants separate the extremes reasonably but say little about anyone in the middle. Almost all of this work is in men of European ancestry; female pattern hair loss has a partly different genetic basis.',
    sources: 'GWAS Catalog (EFO_0004199 androgenetic alopecia); Richards et al. 2008 Nat Genet 40:1282; Hillmer et al. 2008 Nat Genet 40:1279; Yap et al. 2018 Nat Commun 9:5407 (UK Biobank); Zhuo et al. 2012 Clin Exp Dermatol 37:104 (AR StuI meta-analysis)',
    markers: [
      {
        rsid: 'rs6152',
        gene: 'AR / EDA2R',
        alleles: ['G', 'A'],
        effectAllele: 'G',
        direction: 'risk',
        effect:
          "The 'StuI' synonymous variant in androgen receptor exon 1, the first replicated baldness association. Meta-analysis puts the G allele at roughly 2.7× the odds of androgenetic alopecia in men of European ancestry, with a stronger association for earlier and more severe balding.",
        note:
          'X-linked, so men carry one copy and women two — the marker only carries its usual meaning in men. X-linked, so men carry one copy and women two — the marker only carries its usual meaning in men. G is the common allele: about 85% of men of European ancestry carry it (~99% in East Asians, but only about 37% in African-ancestry groups), so this variant is better at identifying the small minority unlikely to bald than at flagging those who will. The causal variant in this region has not been pinned down; rs6152 is a tag, and neighbouring EDA2R variants tag it too. One Indonesian study found no association, so ancestry generalisation is not established.',
      },
      {
        rsid: 'rs1160312',
        gene: '20p11 (PAX1–FOXA2 region)',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'The strongest autosomal baldness signal, in a gene desert on 20p11.22. Each A copy is associated with roughly 1.6× the odds of androgenetic alopecia (p≈1e-14, risk-allele frequency about 43%), and the same allele shows a clear dose effect on balding scores in UK Biobank (p≈3e-98).',
        note:
          'Independent of the androgen receptor locus; carrying risk alleles at both is associated with substantially higher odds than either alone. Neighbouring 20p11 variants such as rs6113491 tag the same signal and add little.',
      },
    ],
  },
  {
    id: 'alopecia-areata',
    name: 'Alopecia areata',
    aliases: ['patchy hair loss', 'autoimmune hair loss', 'alopecia totalis', 'alopecia universalis', 'IL2RA'],
    category: 'dermatologic',
    impact: 'modest',
    summary:
      'An autoimmune condition in which T cells attack the hair follicle, causing round patches of hair loss that can progress to complete scalp or body hair loss. Genetically it looks much more like type 1 diabetes and rheumatoid arthritis than like pattern baldness — the loci are immune-regulatory, centred on T-cell activation and NKG2D ligands. Lifetime risk is around 2%.',
    caveat:
      'The dominant signal is in the HLA region, which consumer arrays cover too sparsely to genotype meaningfully; the markers below are non-HLA loci with small effects and cannot substitute for it. The founding GWAS and its follow-ups were in European-ancestry cohorts of a few thousand cases, so effect estimates are less settled than for larger diseases. Nothing here predicts whether patchy disease will progress or regrow.',
    sources: 'GWAS Catalog (EFO_0004192); Petukhova et al. 2010 Nature 466:113; Betz et al. 2015 Nat Commun 6:5966',
    markers: [
      {
        rsid: 'rs3118470',
        gene: 'IL2RA',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'In the interleukin-2 receptor alpha chain (CD25), which regulatory T cells depend on. The C allele is associated with roughly 1.4× the odds of alopecia areata (p≈8e-21, allele frequency about 32%).',
        note:
          "The same gene is an established locus for type 1 diabetes and multiple sclerosis. Some publications report this variant on the opposite strand as 'G'; on the dbSNP forward strand the risk allele is C.",
      },
      {
        rsid: 'rs2155219',
        gene: 'EMSY / LRRC32 (11q13.5)',
        alleles: ['G', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'The 11q13.5 atopy locus, also associated with hay fever, eczema and ulcerative colitis. The T allele is associated with roughly 1.2× the odds of alopecia areata (p≈4e-08).',
        note:
          'One of the observed genetic overlaps between alopecia areata and atopic disease; atopy is more common in people with alopecia areata than in the general population.',
      },
    ],
  },
  {
    id: 'acne',
    name: 'Acne vulgaris',
    aliases: ['acne', 'spots', 'pimples', 'nodulocystic acne', 'OVOL1', 'sebaceous'],
    category: 'dermatologic',
    impact: 'modest',
    summary:
      'Nearly everyone gets some acne; the genetics studied here is about severe, scarring disease. Twin heritability is high — around 80% — but the loci found so far have small effects and point at hair-follicle and sebaceous-gland biology, and at TGF-beta signalling, rather than at anything dietary or hygienic.',
    caveat:
      'These come from a case-control study of severe (nodulocystic) acne in European-ancestry cohorts, with the two markers below replicated in follow-up work; effect sizes are near 1.2 per allele and are not informative for ordinary teenage acne. The 1q41/TGFB2 locus from the same study is not included here because it was not verified for this entry.',
    sources: 'GWAS Catalog (EFO_0003762); Navarini et al. 2014 Nat Commun 5:4020; Petridis et al. 2018 Nat Commun 9:5075',
    markers: [
      {
        rsid: 'rs478304',
        gene: 'OVOL1 region (11q13.1)',
        alleles: ['G', 'T'],
        effectAllele: 'T',
        direction: 'risk',
        effect:
          'The strongest reported severe-acne locus, near OVOL1, a transcription factor controlling hair-follicle and sebaceous-gland differentiation. Each T copy is associated with roughly 1.2× the odds of severe acne (p≈3e-11).',
        note:
          'Close to a 50/50 allele split, so most people carry at least one copy.',
      },
      {
        rsid: 'rs38055',
        gene: '5q11.2 (intergenic)',
        alleles: ['A', 'G'],
        effectAllele: 'A',
        direction: 'risk',
        effect:
          'An intergenic variant on 5q11.2 with no protein-coding gene nearby; each A copy is associated with roughly 1.18× the odds of severe acne (p≈5e-09).',
        note:
          'About 30% allele frequency in European-ancestry groups, where the association was found, and much rarer in African-ancestry groups (roughly 6-10%). The signal sits in a gene desert and no causal gene has been assigned. The mechanism is unknown — the signal sits in a gene desert, and no causal gene has been assigned.',
      },
    ],
  },
  {
    id: 'keloid',
    name: 'Keloid scarring',
    aliases: ['keloid', 'hypertrophic scar', 'scarring', 'NEDD4'],
    category: 'dermatologic',
    impact: 'modest',
    summary:
      'Keloids are raised scars that grow beyond the boundary of the original wound. They cluster strongly in families and are several times more common in people of African and East Asian ancestry than in people of European ancestry. Only a handful of loci have reached genome-wide significance; the one below is the best replicated.',
    caveat:
      "This is the honest gap in the entry: the association was discovered and replicated in Japanese cohorts, and follow-up in African-ancestry groups — where keloids are most common — has been inconsistent, with some studies failing to confirm it. A single marker also cannot capture a trait this familial. Treat this as one weak clue, not a scarring forecast, and let a surgeon's judgement and personal scar history do the work before elective incisions.",
    sources: 'GWAS Catalog (EFO_0009552); Nakashima et al. 2010 Nat Genet 42:768; Velez Edwards et al. 2014 (African-ancestry follow-up)',
    markers: [
      {
        rsid: 'rs8032158',
        gene: 'NEDD4',
        alleles: ['T', 'C'],
        effectAllele: 'C',
        direction: 'risk',
        effect:
          'Intronic in NEDD4, a ubiquitin ligase involved in fibroblast proliferation and TGF-beta signalling. Each C copy is associated with roughly 1.5× the odds of keloid in Japanese cohorts (p≈6e-13, allele frequency about 33%).',
        note:
          'The same C allele also raises the risk of Dupuytren contracture, another fibro-proliferative condition (beta about +0.17, p about 2e-24 in a 2024 European meta-analysis of 11,320 cases), and of hypertrophic scarring — one of the cleaner shared fibrosis signals here, and notable for replicating in European cohorts where the keloid association itself is less settled.',
      },
    ],
  },
];
